import React, { useState, useRef, useLayoutEffect } from "react";
import {
  getDatabase,
  ref,
  onValue,
  get,
  set,
  push,
  update,
  serverTimestamp,
  query,
  limitToFirst,
} from "firebase/database";
import { useAuth } from "../contexts/AuthContext";
import { useEffect } from "react";
import {
  getUserByID,
  getInquiryDataById,
  getCurrentUserDetails,
  getListingByID,
  updateInquiryStatus,
  getPlatformHourlyRate,
  getBookingByInquiryId,
  sendSMS,
  getListingLockDatesByID,
  getEventsByListingId,
  getBookingSourceData,
  getFullDayLockedDates,
  getLockedTimeConflict,
  sendEmail,
  getUserMetaByID,
  getActiveVerifiedBoatTypes,
  formatTimeDisplay,
  formatDateTimeDisplay,
  formatChatMessageHtml,
} from "./utils";
import { notifyNewBooking } from "./utils/adminNotifications";
import { notifyOwnerNewBooking, notifyOwnerOfferAccepted } from "./utils/userNotifications";
import {
  notifyRenterOfferReceived,
  notifyNewMessage,
} from "./utils/userNotifications";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import CurrencyInput from "react-currency-input-field";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Alert } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import moment from "moment-timezone";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";

import { getPassengerCounts, toPassengerCount } from "./utils/passengerCounts";

const isFishingCharterListing = (listing) =>
  listing?.boatType === "Fishing Charter";

function InquiryGuestLine({ inquiryData }) {
  const pax = getPassengerCounts(inquiryData);
  return (
    <p className="mb-1">
      • {pax.total} Guests (
      {pax.children > 0 && (
        <>
          {" "}
          {pax.children} Children,
        </>
      )}
      {pax.infants > 0 && (
        <>
          {" "}
          {pax.infants} Infants,
        </>
      )}
      {pax.seniors > 0 && (
        <>
          {" "}
          {pax.seniors} Seniors,
        </>
      )}
      {pax.adults > 0 && (
        <>
          {" "}
          {pax.adults} Adults{" "}
        </>
      )}
      )
    </p>
  );
}

const canViewRenterProfile = (viewerRole) =>
  ["Boat Owner", "Admin", "Pro Staff"].includes(viewerRole);

const getRenterProfileUserId = ({
  currentUser,
  currentUserDetails,
  receiverId,
  otherParticipant,
  selectedChat,
  otherParticipants,
}) => {
  if (!canViewRenterProfile(currentUserDetails?.role)) {
    return null;
  }

  if (receiverId && otherParticipant?.role === "Renter") {
    return receiverId;
  }

  if (selectedChat && currentUser?.uid) {
    const otherId =
      selectedChat.senderId === currentUser.uid
        ? selectedChat.receiverId
        : selectedChat.senderId;
    const otherUser = otherParticipants[otherId];
    if (otherUser?.role === "Renter") {
      return otherId;
    }
  }

  return null;
};

const BoatTypeVerifiedBadge = ({ user, userMeta }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const resolvedMeta = userMeta || user?._userMeta || null;
  const verifiedTypes =
    user?.role === "Renter"
      ? getActiveVerifiedBoatTypes(user, resolvedMeta)
      : [];

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  if (verifiedTypes.length === 0) {
    return null;
  }

  const countLabel =
    verifiedTypes.length === 1
      ? "1 boat verified"
      : `${verifiedTypes.length} boats verified`;

  return (
    <span
      ref={wrapperRef}
      className={`user-verified-badge-wrapper${open ? " is-open" : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setOpen((prev) => !prev);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          setOpen((prev) => !prev);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Verified for ${verifiedTypes.length} boat types`}
      aria-expanded={open}
    >
      <span className="user-verified-badge">
        <i className="material-icons" aria-hidden="true">
          check_circle
        </i>
        Verified · {verifiedTypes.length}
      </span>
      <div className="user-verified-badge-popover" role="tooltip">
        <p className="user-verified-badge-popover-title">Verified boats</p>
        <p className="user-verified-badge-popover-count">{countLabel}</p>
        <ul className="user-verified-badge-popover-list">
          {verifiedTypes.map((type) => (
            <li key={type}>{type}</li>
          ))}
        </ul>
      </div>
    </span>
  );
};

export default function Inbox() {
  const scroll = useRef();
  const auth = useAuth();
  const currentUser = auth.currentUser;
  const [receiverId, setReceiverId] = useState("");
  const [message, setMessage] = useState("");
  const [chats, setChats] = useState([]);
  const [otherParticipants, setOtherParticipants] = useState({});
  const [otherParticipant, setOtherParticipant] = useState({});
  const [selectedChat, setSelectedChat] = useState(null);
  const [listingTitle, setlistingTitle] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [sameDayHours, setSameDayHours] = useState(0);
  const [sameDayMinutes, setSameDayMinutes] = useState(0);
  const [overnight, setOvernight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offeredDuration, setOfferedDuration] = useState("0");
  const [arivingDateTime, setArivingDateTime] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentUserDetails, setCurrentUserDetails] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersMetaById, setUsersMetaById] = useState({});
  const [userLimit, setUserLimit] = useState(20);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);

  const [offerAddresses, setOfferAddresses] = useState({
    address: {
      pickupAddress: "",
      dropOffAddress: "",
    },
    code: {
      pickupCode: {},
      dropOffCode: {},
    },
  });

  const handleOfferedTime = (value) => {
    setOfferedTime(value);
  };

  const handleSameDayHours = (value) => {
    setSameDayHours(value);
  };

  const convertTime = (time12h) => {
    if (!time12h) return "";

    const normalizedTime = String(time12h).trim();
    const twentyFourHourMatch = normalizedTime.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (twentyFourHourMatch) {
      return `${twentyFourHourMatch[1].padStart(2, "0")}:${twentyFourHourMatch[2]}`;
    }

    const timeMatch = normalizedTime.match(/^(\d{1,2}):(\d{2})(?::|\s*)(am|pm)$/i);
    if (!timeMatch) return "";

    // Extract hours, minutes, and modifier
    let hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const modifier = timeMatch[3].toLowerCase(); // Convert to lowercase to handle 'PM', 'pm', 'AM', 'am'

    // Handle special cases for 12 AM/PM
    if (hours === 12) {
      hours = modifier === "pm" ? 12 : 0;
    } else if (modifier === "pm") {
      hours += 12;
    }

    // Return formatted 24-hour time
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  const handleOvernight = (value) => {
    if (value > 0) {
      setSameDayHours(0);
    }

    setOvernight(value);
  };

  const Navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [urlParamProcessed, setUrlParamProcessed] = useState(false);

  // Handle URL query parameter to pre-select a conversation
  useEffect(() => {
    const initializeFromUrlParam = async () => {
      const userIdParam = searchParams.get("userId");

      // Only process if we have a userId param, haven't processed it yet, and have currentUser
      if (!userIdParam || urlParamProcessed || !currentUser) return;

      // Prevent messaging yourself
      if (userIdParam === currentUser.uid) {
        setUrlParamProcessed(true);
        return;
      }

      try {
        // Fetch the target user details to verify they exist
        const targetUserDetails = await getUserByID(userIdParam);
        if (!targetUserDetails) {
          console.error("User not found:", userIdParam);
          setUrlParamProcessed(true);
          return;
        }

        // Set the receiver ID to enable messaging this user
        setReceiverId(userIdParam);
        setOtherParticipant(targetUserDetails);

        // Add target user to otherParticipants for display in chat list
        setOtherParticipants(prev => ({
          ...prev,
          [userIdParam]: targetUserDetails
        }));

        setUrlParamProcessed(true);
      } catch (error) {
        console.error("Error initializing chat from URL param:", error);
        setUrlParamProcessed(true);
      }
    };

    initializeFromUrlParam();
  }, [searchParams, currentUser, urlParamProcessed]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (currentUser) {
        const details = await getCurrentUserDetails();
        setCurrentUserDetails(details);
      }
    };
    fetchUserDetails();
  }, [currentUser]);

  useEffect(() => {
    if (currentUserDetails?.role === "Admin") {
      const database = getDatabase();
      const usersRef = query(ref(database, "users"), limitToFirst(userLimit));

      const unsubscribe = onValue(usersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const userList = Object.keys(data).map(uid => ({
            ...data[uid],
            uid
          }));
          setAllUsers(userList);
          setHasMoreUsers(userList.length === userLimit);
        } else {
          setAllUsers([]);
          setHasMoreUsers(false);
        }
      });

      return () => unsubscribe();
    }
  }, [currentUserDetails, userLimit]);

  useEffect(() => {
    if (currentUserDetails?.role !== "Admin") {
      setUsersMetaById({});
      return undefined;
    }

    const database = getDatabase();
    const userMetaRef = ref(database, "userMeta");

    const unsubscribe = onValue(userMetaRef, (snapshot) => {
      const data = snapshot.val() || {};
      setUsersMetaById(data);
    });

    return () => unsubscribe();
  }, [currentUserDetails?.role]);

  // Global search effect for Admin
  useEffect(() => {
    if (currentUserDetails?.role === "Admin" && searchTerm.length > 2) {
      setIsSearching(true);
      setLoading(true);

      const database = getDatabase();
      const timer = setTimeout(async () => {
        try {
          const usersSnapshot = await get(ref(database, "users"));
          const allUsersData = usersSnapshot.val();

          if (allUsersData) {
            const matches = [];
            Object.keys(allUsersData).forEach(uid => {
              const user = allUsersData[uid];
              const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
              const email = (user.email || "").toLowerCase();
              if (fullName.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase())) {
                matches.push({ ...user, uid });
              }
            });
            setSearchResults(matches);
          } else {
            setSearchResults([]);
          }
        } catch (error) {
          console.error("Global search failed:", error);
        } finally {
          setIsSearching(false);
          setLoading(false);
        }
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  }, [searchTerm, currentUserDetails?.role]);

  // Update selected chat when chats load and we have a receiverId from URL param
  useEffect(() => {
    if (!receiverId || !chats.length || selectedChat) return;

    // Look for existing chat with this user
    const existingChat = chats.find(
      (chat) =>
        (currentUser.uid === chat.senderId && receiverId === chat.receiverId) ||
        (currentUser.uid === chat.receiverId && receiverId === chat.senderId)
    );

    if (existingChat) {
      setSelectedChat(existingChat);
    }
  }, [chats, receiverId, currentUser, selectedChat]);

  /* Scroll & Pagination State */
  const chatBodyRef = useRef();
  const [visibleLimit, setVisibleLimit] = useState(50);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);

  // Reset limit when chat changes
  useEffect(() => {
    setVisibleLimit(50);
  }, [selectedChat?.chatId]);

  // Auto-scroll to bottom on initial load — useLayoutEffect fires BEFORE paint
  useLayoutEffect(() => {
    if (chatBodyRef.current && visibleLimit === 50) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
      // Second scroll after DOM fully settles (complex offer cards may take longer to render)
      setTimeout(() => {
        if (chatBodyRef.current) {
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      }, 150);
    }
  }, [selectedChat?.chatId, selectedChat?.messages?.length]);

  // Handle scroll to top to load more
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight } = e.target;
    if (scrollTop === 0 && selectedChat?.messages?.length > visibleLimit) {
      setPrevScrollHeight(scrollHeight);
      setVisibleLimit((prev) => Math.min(prev + 50, selectedChat.messages.length));
    }
  };

  // Restore scroll position after loading more
  useLayoutEffect(() => {
    if (prevScrollHeight > 0 && chatBodyRef.current) {
      const newScrollHeight = chatBodyRef.current.scrollHeight;
      chatBodyRef.current.scrollTop = newScrollHeight - prevScrollHeight;
      setPrevScrollHeight(0);
    }
  }, [visibleLimit, prevScrollHeight]);

  const handleSidebarScroll = (e) => {
    if (currentUserDetails?.role !== "Admin") return;

    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMoreUsers && !loading) {
      setUserLimit(prev => prev + 20);
    }
  };

  const [currentMessageListingDetails, setCurrentMessageListingDetails] =
    useState([]);

  const [deliveryPickupCheckbox, setDeliveryPickupCheckbox] = useState(false);
  const handleDeliveryPickupCheckboxChange = (event) => {
    setDeliveryPickupCheckbox(event.target.checked);
  };

  const [trailerCheckbox, setTrailerCheckbox] = useState(false);

  const handleTrailerCheckbox = (event) => {
    setTrailerCheckbox(event.target.checked);
  };
  const [captainCheckbox, setCaptainCheckbox] = useState(false);
  const [additionalCost, setAdditionalCost] = useState(null);

  const handleAdditionalCost = (value) => {
    setAdditionalCost(value);

    if (value <= 0 || !value) {
      setAdditionalCost(0);
    }
  };

  const [ownerDiscount, setOwnerDiscount] = useState(0);
  const handleOwnerDiscount = (val) => {
    let value;
    if (!isNaN(val)) {
      value = parseFloat(val);
    }
    let maxAllowedValue = 0;

    if (value === "" || value === undefined || value === "undefined") {
      setOwnerDiscount(0);
      return;
    }

    if (overnight == 1 || (sameDayHours >= 24 && sameDayHours < 48)) {
      maxAllowedValue = 100;
      if (value <= maxAllowedValue) {
        setOwnerDiscount(value);
        setError("");
      } else if (value >= maxAllowedValue) {
        setError("Maximum discount: $100");
      } else {
        setError("");
        setOwnerDiscount(value);
      }
    } else if (overnight >= 2 || sameDayHours >= 48) {
      maxAllowedValue = 200;
      if (value <= maxAllowedValue) {
        setOwnerDiscount(value);
        setError("");
      } else if (value >= maxAllowedValue) {
        setError("Maximum discount: $200");
      } else {
        setOwnerDiscount(value);
        setError("");
      }
    } else {
      setOwnerDiscount(value);
    }
  };

  const handleCaptainCheckbox = (event) => {
    setCaptainCheckbox(event.target.checked);
  };

  const database = getDatabase();

  const selectContact = async (newReceiverId) => {
    // Function to set the receiver ID when a contact is selected
    setReceiverId(newReceiverId);
    setMessage("");
    try {
      // Await the result of getUserByID before setting otherParticipant
      const [otherParticipantDetails, participantMeta] = await Promise.all([
        getUserByID(newReceiverId),
        getUserMetaByID(newReceiverId),
      ]);
      setOtherParticipant({
        ...otherParticipantDetails,
        _userMeta: participantMeta,
      });

      // Find the chat for the selected contact
      const chatFound = chats.find(
        (chat) =>
          (currentUser.uid === chat.senderId &&
            newReceiverId === chat.receiverId) ||
          (currentUser.uid === chat.receiverId &&
            newReceiverId === chat.senderId)
      );
      if (chatFound) {
        const chatRef = ref(getDatabase(), `chats/${chatFound.chatId}`);
        const selectedChatSnapshot = await get(chatRef);
        const selectedChatData = selectedChatSnapshot.val();
        if (
          selectedChatData &&
          selectedChatData.lastSenderId !== currentUser.uid
        ) {
          const updates = {};
          if (selectedChatData.senderId === currentUser.uid) {
            updates.senderReadStatus = 1;
          } else {
            updates.receiverReadStatus = 1;
          }
          await update(chatRef, updates);
        }
        // Set the selected chat in the state
        setSelectedChat(chatFound);
      } else {
        setSelectedChat(null);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const getLocationName = async (lat, lng) => {
    try {
      const dropoffLat = lat;
      const dropoffLlng = lng;

      const dropOffResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${dropoffLat},${dropoffLlng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
      );

      const dropOffdata = await dropOffResponse.json();
      console.log("dropOffdatazzzzzzzzzzzzzzzzzzzzzzzz", dropOffResponse);
      const dropoffName =
        dropOffdata.results[0]?.formatted_address || "Location not found";

      return dropoffName;
    } catch (error) {
      console.error("Error fetching location name:", error);
      return null;
    }
  };

  const handleAdd = (field) => {
    if (field === "sameDayHours") {
      setSameDayHours(Number(sameDayHours) + 1);
      setOvernight(0);
    } else if (field === "sameDayMinutes") {
      setSameDayMinutes(sameDayMinutes + 1);
      setOvernight(0);
    } else {
      // If user selects overnight, set hours and minutes to 0
      setSameDayHours(0);
      setSameDayMinutes(0);
      setOvernight(overnight + 1);
    }
  };

  const handleRemove = (field) => {
    if (field === "sameDayHours" && sameDayHours > 0) {
      setSameDayHours(sameDayHours - 1);
    } else if (field === "sameDayMinutes" && sameDayMinutes > 0) {
      setSameDayMinutes(sameDayMinutes - 1);
    } else if (overnight > 0) {
      setOvernight(overnight - 1);
    }
  };

  const [otherUserDetails, setOtherUserDetails] = useState([]);

  useEffect(() => {
    const fetchOtherUserDetails = async () => {
      try {
        const userDetails = await getCurrentUserDetails();
        setOtherUserDetails(userDetails);
        const rateHourly = await getPlatformHourlyRate();

        if (currentMessageListingDetails.pricing?.hourlyRate) {
        }
      } catch (error) {
        // Handle error, e.g., redirect to an error page
        console.error("Error fetching other user details", error);
      }
    };

    fetchOtherUserDetails();
  }, []);

  useEffect(() => {
    // Update the button state when listingTitle changes
    // You can modify this condition based on your specific logic
    if (listingTitle !== "") {
      // Enable the button when listingTitle is not empty
      // You might need to adjust the condition based on your specific requirements
      setButtonDisabled(false);
    }
  }, [listingTitle]);

  useEffect(() => {
    const fetchUserDetails = async (participantIds) => {
      const participantsDetails = {};
      await Promise.all(
        participantIds.map(async (id) => {
          if (!participantsDetails[id]) {
            const [userDetails, userMeta] = await Promise.all([
              getUserByID(id),
              getUserMetaByID(id),
            ]);
            participantsDetails[id] = { ...userDetails, _userMeta: userMeta };
          }
        })
      );
      setOtherParticipants(participantsDetails);
    };

    const database = getDatabase();
    const chatsRef = ref(database, `chats`);

    const unsubscribe = onValue(chatsRef, async (snapshot) => {
      const chatData = snapshot.val() || {};
      const userChats = Object.keys(chatData).map((chatId) => ({
        chatId,
        senderId: chatData[chatId].senderId,
        receiverReadStatus: chatData[chatId].receiverReadStatus,
        senderReadStatus: chatData[chatId].senderReadStatus,
        lastSenderId: chatData[chatId].lastSenderId,
        receiverId: chatData[chatId].receiverId,
        messages: chatData[chatId].messages || [],
        lastMessageTime: chatData[chatId].lastMessageTime || 
                         (chatData[chatId].messages && chatData[chatId].messages.length > 0 
                             ? chatData[chatId].messages[chatData[chatId].messages.length - 1].timestamp 
                             : "1970-01-01T00:00:00.000Z")
      })).sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));

      setChats(userChats);

      // Update the selected chat when chats change
      if (receiverId) {
        const updatedSelectedChat = userChats.find(
          (chat) =>
            (currentUser.uid === chat.senderId &&
              receiverId === chat.receiverId) ||
            (currentUser.uid === chat.receiverId &&
              receiverId === chat.senderId)
        );
        setSelectedChat(updatedSelectedChat);
      }

      // Collect participant IDs for fetching details
      const participantIds = userChats.reduce((ids, chat) => {
        const isParticipant =
          currentUser.uid === chat.senderId || currentUser.uid === chat.receiverId;

        if (currentUserDetails?.role === "Admin") {
          // Admins need details for BOTH participants to show "A & B"
          if (!ids.includes(chat.senderId)) ids.push(chat.senderId);
          if (!ids.includes(chat.receiverId)) ids.push(chat.receiverId);
        } else if (isParticipant) {
          // Non-admins only need the OTHER participant
          const otherId =
            currentUser.uid === chat.senderId ? chat.receiverId : chat.senderId;
          if (!ids.includes(otherId)) ids.push(otherId);
        }
        return ids;
      }, []);

      // Fetch user details only if there are new participants
      if (participantIds.length > 0) {
        await fetchUserDetails(participantIds);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser, receiverId]);

  const formatDate = (inputDate) => {
    try {
      const dateObj = new Date(inputDate);
      const year = dateObj.getFullYear();
      const month = (dateObj.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
      const day = dateObj.getDate().toString().padStart(2, "0");

      const formattedDate = `${year}-${month}-${day}`;

      return formattedDate;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  const [error, setError] = useState("");

  const getTimestamp = (chat) => {
    if (!chat || !chat.messages || chat.messages.length === 0) return 0;
    const lastMessage = chat.messages[chat.messages.length - 1];
    return lastMessage && lastMessage.timestamp ? new Date(lastMessage.timestamp).getTime() : 0;
  };

  const calculateEstimatedPricing = () => {
    const inquiryHours = sameDayHours;
    const inquiryovernight = overnight;
    let pricing = 0;

    const getDailyPriceFromBoatDetails = (day) => {
      let filteredDay =
        currentMessageListingDetails?.pricing?.dailyOptions?.filter(
          (P) => P?.days == day
        );

      if (filteredDay?.length > 0) {
        return Number(filteredDay[0]?.price) || 0;
      }
    };
    const getHourlyPriceFromBoatDetails = (hour) => {
      let filteredPrice =
        currentMessageListingDetails?.pricing?.hourlyOptions?.filter(
          (P) => P?.hours == hour
        );

      if (filteredPrice?.length > 0) {
        return Number(filteredPrice[0]?.price) || 0;
      } else {
        return hour * currentMessageListingDetails?.pricing?.hourlyRate || 0;
      }
    };

    // Determine base pricing based on inquiry hours
    if (inquiryHours) {
      pricing =
        getHourlyPriceFromBoatDetails(sameDayHours) -
        (ownerDiscount || 0) +
        (Number(additionalCost) || 0);
    }

    // Apply overnight charges
    if (overnight) {
      pricing =
        getDailyPriceFromBoatDetails(overnight) -
        (ownerDiscount || 0) +
        (Number(additionalCost) || 0);
    } else {
      if (inquiryovernight >= 1 && inquiryovernight <= 6) {
        const basePrices = [400, 600, 700, 800, 900, 1000];
        pricing += basePrices[inquiryovernight - 1]; // Assuming overnight prices are additional
      } else if (inquiryovernight >= 7) {
        const lastPrice = 1100; // Update with the actual last price
        pricing += lastPrice + (inquiryovernight - 6) * 100;
      }
    }

    if (
      currentMessageListingDetails?.boatType?.toLowerCase() ===
      "kayak".toLowerCase()
    ) {
      // Determine base pricing based on inquiry hours
      if (inquiryHours) {
        pricing =
          getHourlyPriceFromBoatDetails(sameDayHours) *
          inquiryDetails?.inquiryData?.numberOfBoats -
          (ownerDiscount || 0) +
          (Number(additionalCost) || 0);
      }

      // Apply overnight charges
      if (overnight) {
        pricing =
          getDailyPriceFromBoatDetails(overnight) *
          inquiryDetails?.inquiryData?.numberOfBoats -
          (ownerDiscount || 0) +
          (Number(additionalCost) || 0);
      }
    }

    if (
      currentMessageListingDetails?.boatType?.toLowerCase() ===
      "jetski".toLowerCase()
    ) {
      // Determine base pricing based on inquiry hours
      if (inquiryHours) {
        pricing =
          getHourlyPriceFromBoatDetails(sameDayHours) *
          inquiryDetails?.inquiryData?.numberOfBoats -
          (ownerDiscount || 0) +
          (Number(additionalCost) || 0);
      }

      // Apply overnight charges
      if (overnight) {
        pricing =
          getDailyPriceFromBoatDetails(overnight) *
          inquiryDetails?.inquiryData?.numberOfBoats -
          (ownerDiscount || 0) +
          (Number(additionalCost) || 0);
      }
    }

    return parseInt(pricing) || 0;
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    if (message.trim() === "") return;

    const database = getDatabase();

    const [firstUserId, secondUserId] =
      currentUser.uid < receiverId
        ? [currentUser.uid, receiverId]
        : [receiverId, currentUser.uid];

    const chatId1 = `${firstUserId}_${secondUserId}`;
    const chatId2 = `${secondUserId}_${firstUserId}`;

    const messageData = {
      senderId: currentUser.uid,
      receiverId: receiverId,
      message,
      timestamp: serverTimestamp(),
    };

    const chatRef1 = ref(database, `chats/${chatId1}`);
    const chatRef2 = ref(database, `chats/${chatId2}`);

    try {
      const chatSnapshot1 = await get(chatRef1);
      const chatSnapshot2 = await get(chatRef2);
      let usedChatId;
      let usedChatRef;

      if (chatSnapshot1.exists()) {
        usedChatId = chatId1;
        usedChatRef = chatRef1;
        const chatData = chatSnapshot1.val();
        const updates = {
          lastSenderId: currentUser.uid,
          messages: [...chatData.messages, messageData],
        };
        if (chatData.senderId === currentUser.uid) {
          updates.senderReadStatus = 1;
          updates.receiverReadStatus = 0;
        } else {
          updates.senderReadStatus = 0;
          updates.receiverReadStatus = 1;
        }
        await update(chatRef1, updates);
      } else if (chatSnapshot2.exists()) {
        usedChatId = chatId2;
        usedChatRef = chatRef2;
        const chatData = chatSnapshot2.val();
        const updates = {
          lastSenderId: currentUser.uid,
          messages: [...chatData.messages, messageData],
        };
        if (chatData.senderId === currentUser.uid) {
          updates.senderReadStatus = 1;
          updates.receiverReadStatus = 0;
        } else {
          updates.senderReadStatus = 0;
          updates.receiverReadStatus = 1;
        }
        await update(chatRef2, updates);
      } else {
        usedChatId = chatId1;
        usedChatRef = chatRef1;
        await set(chatRef1, {
          senderId: currentUser.uid,
          receiverId,
          senderReadStatus: 1,
          receiverReadStatus: 0,
          lastSenderId: currentUser.uid,
          messages: [messageData],
        });
      }

      setMessage("");

      const senderDetails = await getCurrentUserDetails();
      const senderName = senderDetails
        ? `${senderDetails.firstName || ""} ${senderDetails.lastName || ""}`.trim()
        : "";
      notifyNewMessage(
        receiverId,
        senderName,
        currentUser.uid,
        message
      ).catch((err) =>
        console.error("Failed to create message notification:", err)
      );

      // Hit the server's endpoint
      fetch(`${process.env.REACT_APP_BASE_URL}/schedule-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          senderId: currentUser.uid,
          receiverId,
          lastSenderId: currentUser.uid,
          chatId: usedChatId,
        }),
      }).catch(err => console.error("Error scheduling message:", err));

      setTimeout(() => {
        if (chatBodyRef.current) {
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
      }, 150);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  async function addOfferToInquiry(inquiryId, newOffer) {
    const database = getDatabase();
    const inquiryRef = ref(database, `inquiries/${inquiryId}`);
    const updateInquiryDate = ref(
      database,
      `inquiries/${inquiryId}/inquiryData/prefferedDate`
    );

    try {
      let outputDate = new Date(`${offeredDate}`).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      // await set(updateInquiryDate, outputDate);
      const inquirySnapshot = await get(inquiryRef);
      const existingInquiryData = inquirySnapshot.val();

      if (!existingInquiryData) {
        throw new Error("Inquiry not found");
      }

      const updatedInquiryData = {
        ...existingInquiryData,
        offerData: newOffer,
      };

      await set(inquiryRef, updatedInquiryData);
    } catch (error) {
      console.error("Error adding offer to inquiry:", error);
      throw error; // Re-throw the error to handle it elsewhere if needed
    }
  }

  async function editOfferToInquiry(inquiryId, newOffer) {
    const database = getDatabase();
    const offerRef = ref(database, `inquiries/${inquiryId}/offerData`);
    const updateInquiryDate = ref(
      database,
      `inquiries/${inquiryId}/inquiryData/prefferedDate`
    );

    try {
      let outputDate = new Date(`${offeredDate}`).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      // await set(updateInquiryDate, outputDate);
      await set(offerRef, newOffer);

      // Fetch and log the updated offer data
      const updatedSnapshot = await get(offerRef);
      if (updatedSnapshot.exists()) {
      }
    } catch (error) {
      console.error("Error updating offer:", error);
      throw error;
    }
  }

  const [isCreateOfferModalOpen, setCreateOfferModalOpen] = useState(false);
  const [isEditOfferModalOpen, setEditOfferModalOpen] = useState(false);
  const [showPayNowModal, setShowPayNowModal] = useState(false);
  const [showOfferReceivedGuidanceModal, setShowOfferReceivedGuidanceModal] =
    useState(false);
  const [showOfferSentGuidanceModal, setShowOfferSentGuidanceModal] =
    useState(false);
  const [pendingOfferViewReady, setPendingOfferViewReady] = useState(false);
  const [payNowModalMessage, setPayNowModalMessage] = useState(null);
  const [acceptingEditKey, setAcceptingEditKey] = useState(null);
  const [currentBookingStatus, setCurrentBookingStatus] = useState(null);
  const [offerExpiryToggle, setOfferExpiryToggle] = useState(true);
  const [boatDetailsById, setBoatDetailsById] = useState({});
  const [offeredDate, setOfferedDate] = useState("");
  const [offeredTime, setOfferedTime] = useState("");

  const [inquiryDetails, setInquiryDetails] = useState([]);
  const [currentMessageInquiryId, setCurrentMessageInquiryId] = useState("");

  const openCreateOfferModal = async (id, listingId) => {
    setLoading(true);
    try {
      setCurrentMessageInquiryId(id);
      const inquiryData = await getInquiryDataById(id);
      const offerBoatDetails = await getListingByID(inquiryData?.listingId);
      console.log("offerBoatDetailsvvvvvvvvvvv", offerBoatDetails);
      setBoatDetailsById(offerBoatDetails);
      setDeliveryPickupCheckbox(offerBoatDetails?.deliveryAndPickup);

      setDeliveryPickupCheckbox(offerBoatDetails?.deliveryAndPickup);

      let mainBoatLocation;
      if (offerBoatDetails?.storageAddressText) {
        mainBoatLocation = offerBoatDetails.storageAddressText;
      } else {
        mainBoatLocation = await getLocationName(
          offerBoatDetails?.storageAddress?.lat,
          offerBoatDetails?.storageAddress?.lng
        );

        // Self-healing: Update DB if we had to fetch it
        if (mainBoatLocation && mainBoatLocation !== "Location not found" && offerBoatDetails?.id) {
          const db = getDatabase();
          update(ref(db, `listings/${offerBoatDetails.id}`), {
            storageAddressText: mainBoatLocation
          }).catch(err => console.error("Self-healing failed in Inbox", err));
        }
      }

      setOfferAddresses((prev) => {
        return {
          ...prev,
          address: {
            ...prev.address,
            pickupAddress: mainBoatLocation,
            dropOffAddress: mainBoatLocation,
          },
          code: {
            ...prev.code,
            dropOffCode: offerBoatDetails?.storageAddress,
            pickupCode: offerBoatDetails?.storageAddress,
          },
        };
      });
      console.log("oooooooofer addddressss", offerAddresses);

      // Now you can set the inquiry details in the modal state or directly in the form fields
      // For example, assuming you have state variables like setInquiryDetails
      setInquiryDetails(inquiryData);

      if (inquiryData && inquiryData?.inquiryData.sameDayHours !== undefined) {
        setSameDayHours(inquiryData?.inquiryData?.sameDayHours);
        setOfferedDate(formatDate(inquiryData?.inquiryData.prefferedDate));

        if (inquiryData?.inquiryData.overnight > 0) {
          setOvernight(inquiryData?.inquiryData.overnight);
        }

        const convertedTime = convertTime(
          inquiryData?.inquiryData?.prefferedTime || ""
        );
        handleOfferedTime(convertedTime);
      }
      if (inquiryData && inquiryData.inquiryData.additionals !== undefined) {
        if (inquiryData?.inquiryData.additionals.captain) {
          setCaptainCheckbox(true);
        }
        if (inquiryData?.inquiryData.additionals.deliveryPickup) {
          setDeliveryPickupCheckbox(true);
        }
        if (inquiryData?.inquiryData?.additionals?.trailer) {
          setTrailerCheckbox(true);
        }
      }

      const listingData = await getListingByID(
        inquiryData?.listingId || listingId
      );
      if (!listingData) {
        alert("This listing does not exist anymore.");
        setLoading(false);
        return;
      }
      setCurrentMessageListingDetails(listingData);

      const renterDataPromise = await getUserByID(inquiryData.renter);
      const ownerDataPromise = await getUserByID(inquiryData.owner);
      const listingDataPromise = await getListingByID(inquiryData.listingId);

      const [renterData, ownerData, listingPromiseData] = await Promise.all([
        renterDataPromise,
        ownerDataPromise,
        listingDataPromise,
      ]);

      const renterName = `${renterData.firstName} ${renterData.lastName}`;
      const ownerName = `${ownerData.firstName} ${ownerData.lastName}`;
      const listingName = listingPromiseData?.listingTitle;

      const bookingTitle = listingName + " - " + renterName;
      setlistingTitle(bookingTitle);
      setCreateOfferModalOpen(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching inquiry data", error);
      setLoading(false);
    }
  };

  const [selectedMessageData, setSelectedMessageData] = useState({});
  const openViewOfferModal = async (message) => {
    console.log("Opening view offer modal with message:", message);
    console.log("Message offerStatus:", message.offerStatus);
    console.log("Message status:", message.status);
    setLoading(true);
    try {
      setSelectedMessageData(message);
      setCurrentMessageInquiryId(message.inquiryId);
      const inquiryData = await getInquiryDataById(message?.inquiryId);
      console.log("inquiryData offer info data0000000", inquiryData);
      const getBoatDetails = await getListingByID(inquiryData?.listingId);
      setInquiryDetails(inquiryData);

      const dropOffLocations = await getLocationName(
        inquiryData?.offerData?.dropOffAddress?.lat,
        inquiryData?.offerData?.dropOffAddress?.lng
      );
      const pickUpLocations = await getLocationName(
        inquiryData?.offerData?.pickupAddress?.lat,
        inquiryData?.offerData?.pickupAddress?.lng
      );

      setOfferAddresses((prev) => {
        return {
          ...prev,
          address: {
            ...prev.address,
            pickupAddress: pickUpLocations,
            dropOffAddress: dropOffLocations,
          },
          code: {
            ...prev.code,
            dropOffCode: inquiryData?.offerData?.dropOffAddress,
            pickupCode: inquiryData?.offerData?.pickupAddress,
          },
        };
      });

      const renterDataPromise = await getCurrentUserDetails();
      const listingDataPromise = await getListingByID(
        inquiryData?.listingId || inquiryData?.listingID
      );

      setCurrentMessageListingDetails(listingDataPromise);

      const [renterData, listingPromiseData] = await Promise.all([
        renterDataPromise,
        listingDataPromise,
      ]);

      const renterName = `${renterData.firstName} ${renterData.lastName}`;
      const listingName = listingPromiseData.listingTitle;

      const bookingTitle = listingName + " - " + renterName;

      setlistingTitle(bookingTitle);
      const offerGuidanceKey = `offerReceivedGuidanceSeen_${message.inquiryId}`;
      if (!localStorage.getItem(offerGuidanceKey)) {
        setPendingOfferViewReady(true);
        setShowOfferReceivedGuidanceModal(true);
      } else {
        setCreateOfferModalOpen(true);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching inquiry data", error);
      setLoading(false);
      // Handle the error accordingly
    }
  };

  const openEditOfferModal = async (message) => {
    console.log("Opening edit offer modal with message:", message);
    console.log("Message offerStatus:", message.offerStatus);
    console.log("Message status:", message.status);
    setLoading(true);
    try {
      setSelectedMessageData(message);
      setCurrentMessageInquiryId(message.inquiryId);
      const inquiryData = await getInquiryDataById(message.inquiryId);
      const listingData = await getListingByID(message.listingId);
      setInquiryDetails(inquiryData);

      const dropOffLocations = await getLocationName(
        inquiryData?.offerData?.dropOffAddress?.lat,
        inquiryData?.offerData?.dropOffAddress?.lng
      );
      const pickUpLocations = await getLocationName(
        inquiryData?.offerData?.pickupAddress?.lat,
        inquiryData?.offerData?.pickupAddress?.lng
      );

      setOfferAddresses((prev) => {
        return {
          ...prev,
          address: {
            ...prev.address,
            pickupAddress: pickUpLocations,
            dropOffAddress: dropOffLocations,
          },
          code: {
            ...prev.code,
            dropOffCode: inquiryData?.offerData?.dropOffAddress,
            pickupCode: inquiryData?.offerData?.pickupAddress,
          },
        };
      });

      setAdditionalCost(inquiryData?.offerData?.additionalCost);
      setTrailerCheckbox(inquiryData?.offerData?.trailerCheckbox);
      setCaptainCheckbox(inquiryData?.offerData?.captainCheckbox);
      setDeliveryPickupCheckbox(inquiryData?.offerData?.deliveryPickup);
      setSameDayHours(inquiryData?.offerData?.offeredHours);
      setOfferedDate(inquiryData?.offerData?.offeredDate);
      setOfferedTime(inquiryData?.offerData?.offeredTime);

      console.log("offeredDateofferedDateofferedDate1", inquiryData);

      // handleSameDayHours(inquiryData?.offerData?.offeredHours);
      handleOvernight(inquiryData?.offerData?.overnight);
      setOwnerDiscount(inquiryData?.offerData?.discount);
      handlePSelect(pickUpLocations);
      handleDropOffSelect(dropOffLocations);

      const renterDataPromise = await getCurrentUserDetails();
      const listingDataPromise = await getListingByID(inquiryData?.listingId);
      setCurrentMessageListingDetails(listingDataPromise);
      const [renterData, listingPromiseData] = await Promise.all([
        renterDataPromise,
        listingDataPromise,
      ]);

      const renterName = `${renterData.firstName} ${renterData.lastName}`;
      const listingName = listingPromiseData.listingTitle;

      const bookingTitle = listingName + " - " + renterName;

      setlistingTitle(bookingTitle);
      setEditOfferModalOpen(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching inquiry data", error);
      setLoading(false);
    }
  };

  const handlePSelect = async (selectedAddress) => {
    try {
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);

      setOfferAddresses((prev) => {
        return {
          ...prev,
          address: {
            ...prev.address,
            pickupAddress: results[0].formatted_address,
          },
          code: { ...prev.code, pickupCode: latLng },
        };
      });
    } catch (error) {
      console.error("Error fetching geolocation:", error);
    }
  };

  const handleDropOffSelect = async (selectedAddress) => {
    try {
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);
      setOfferAddresses((prev) => {
        return {
          ...prev,
          address: {
            ...prev.address,
            dropOffAddress: results[0].formatted_address,
          },
          code: { ...prev.code, dropOffCode: latLng },
        };
      });
    } catch (error) {
      console.error("Error fetching geolocation:", error);
    }
  };

  const closeCreateOfferModal = () => {
    setSelectedMessageData([]);
    setCurrentMessageInquiryId(null);
    setInquiryDetails([]);
    setOfferedDate(null);
    setOfferedTime(null);
    handleSameDayHours(null);
    handleOvernight(null);

    setAdditionalCost(0);
    handleOwnerDiscount(0);
    setTrailerCheckbox(false);
    setCaptainCheckbox(false);
    setDeliveryPickupCheckbox(false);

    setCurrentMessageListingDetails([]);
    setCreateOfferModalOpen(false);
    setPendingOfferViewReady(false);
    setOfferAddresses({
      address: {
        pickupAddress: "",
        dropOffAddress: "",
      },
      code: {
        pickupCode: "",
        dropOffCode: "",
      },
    });
  };

  const closeOfferReceivedGuidanceModal = (openOfferModal = false) => {
    if (currentMessageInquiryId) {
      localStorage.setItem(
        `offerReceivedGuidanceSeen_${currentMessageInquiryId}`,
        "1"
      );
    }
    setShowOfferReceivedGuidanceModal(false);
    if (openOfferModal && pendingOfferViewReady) {
      setPendingOfferViewReady(false);
      setCreateOfferModalOpen(true);
    } else {
      setPendingOfferViewReady(false);
    }
  };

  const closeEditOfferModal = () => {
    setSelectedMessageData([]);
    setCurrentMessageInquiryId(null);
    setInquiryDetails([]);
    setOfferedDate(null);
    setOfferedTime(null);
    handleSameDayHours(null);
    handleOvernight(null);

    setAdditionalCost(0);
    handleOwnerDiscount(0);
    setTrailerCheckbox(false);
    setCaptainCheckbox(false);
    setDeliveryPickupCheckbox(false);

    setCurrentMessageListingDetails([]);
    setEditOfferModalOpen(false);
    setOfferAddresses({
      address: {
        pickupAddress: "",
        dropOffAddress: "",
      },
      code: {
        pickupCode: "",
        dropOffCode: "",
      },
    });
  };

  const handleToggleChange = () => {
    setOfferExpiryToggle(!offerExpiryToggle);
  };

  const sendOfferMessage = async () => {
    const inquiryData = await getInquiryDataById(inquiryDetails.id);
    // Now you can set the inquiry details in the modal state or directly in the form fields
    // For example, assuming you have state variables like setInquiryDetails
    setInquiryDetails(inquiryData);

    let setDuration = "";

    if (parseInt(overnight) === 1) {
      setDuration = "1 Night";
    }

    if (parseInt(overnight) > 1) {
      setDuration = `${overnight} Days`;
    }

    if (parseInt(sameDayHours) === 1) {
      setDuration = "1 Hour";
    }

    if (parseInt(sameDayHours) > 1) {
      setDuration = `${sameDayHours} Hours`;
    }

    const offerMessage = `Hey, I have reviewed your requirements and would like to propose the following offer:
    LineBreakLineBreak<span style="font-weight: bold;">Offer Details:</span>LineBreak
    • $${calculateEstimatedPricing() || 0} PriceLineBreak
    • Time offered, ${inquiryData?.offerData?.offeredDate} at ${formatTimeDisplay(
      inquiryData?.offerData?.offeredTime
    )} LineBreak
    • Duration, ${setDuration}LineBreak
    • Pickup Location, ${offerAddresses.address.pickupAddress}LineBreak
    • Dropoff Location, ${offerAddresses.address.dropOffAddress}`;

    const offerMessageMobile = `Hey, I have reviewed your requirements and would like to propose the following offer:\n\n
Offer Details:\n
• Price, $${calculateEstimatedPricing() || 0}\n
• Time offered: ${inquiryData?.offerData?.offeredDate} at ${formatTimeDisplay(
      inquiryData?.offerData?.offeredTime
    )}\n
• Duration, ${setDuration || "Not specified"}\n
• Pickup Location, ${offerAddresses.address.pickupAddress || "Not specified"}\n
• Dropoff Location, ${offerAddresses.address.dropOffAddress || "Not specified"
      }`;

    const database = getDatabase();

    const chatRef = ref(database, `chats/${selectedChat.chatId}`);

    try {
      const chatSnapshot = await get(chatRef);

      const messageData = {
        senderId: currentUser.uid,
        receiverId: receiverId,
        message: offerMessage,
        messageMobile: offerMessageMobile,
        inquiryId: inquiryDetails.id,
        listingId: inquiryDetails.listingId,
        offer: 1,
        timestamp: serverTimestamp(),
      };

      const chatData = chatSnapshot.val();
      const updates = {
        lastSenderId: currentUser.uid,
        messages: [...chatData.messages, messageData],
      };
      if (chatData.senderId === currentUser.uid) {
        updates.senderReadStatus = 1;
        updates.receiverReadStatus = 0;
      } else {
        updates.senderReadStatus = 0;
        updates.receiverReadStatus = 1;
      }

      await update(chatRef, updates);

      const emailData = await getUserByID(receiverId);
      const currentUserData = await getCurrentUserDetails();
      // const ownerEmail = emailData.email;
      // const subjectEmail = 'New Offer Recieved';
      const ownerName =
        currentUserData.firstName + " " + currentUserData.lastName;

      // const emailBody = await NewOfferRecievedEmailBody(ownerName, listingTitle);
      // sendEmail (ownerEmail, subjectEmail, emailBody);

      const smsBody = `Hello ${emailData.firstName} ${emailData.lastName}, your inquiry for ${listingTitle} by ${ownerName} has received a new offer.\n\n Please login to your dashboard to view and accept the offer: ${process.env.REACT_APP_BASE_URL}`;
      const response = sendSMS(emailData.phone, smsBody);

      notifyRenterOfferReceived(
        receiverId,
        listingTitle,
        currentUser.uid,
        inquiryData?.offerData?.isUpdateOffer === true
      ).catch((err) =>
        console.error("Failed to create renter notification:", err)
      );
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const sendAcceptOfferMessage = async () => {
    const inquiryData = await getInquiryDataById(inquiryDetails.id);
    setInquiryDetails(inquiryData);

    const offerMessage = `Hey, I have accepted the offer, quoted <span style="font-weight: bold;">$${inquiryData?.offerData.offerPrice}</span>.
    LineBreakLooking forward to see you on ${inquiryData?.offerData.offeredDate} at ${inquiryData?.offerData.offeredTime}.`;

    const offerMessageMobile = `Hey, I have accepted the offer, quoted $${inquiryData?.offerData.offerPrice}.  
Looking forward to seeing you on ${inquiryData?.offerData.offeredDate} at ${inquiryData?.offerData.offeredTime}.`;

    const database = getDatabase();

    const chatRef = ref(database, `chats/${selectedChat.chatId}`);

    try {
      const chatSnapshot = await get(chatRef);

      const messageData = {
        senderId: currentUser.uid,
        receiverId: receiverId,
        message: offerMessage,
        messageMobile: offerMessageMobile,
        inquiryId: inquiryDetails.id,
        listingId: inquiryDetails.listingId,
        offerAccepted: 1,
        timestamp: serverTimestamp(),
      };

      await getListingByID(inquiryDetails.listingId);

      const renterSmsBody = `Your offer for ${listingTitle} has been acccepted.\n\n To finalize your booking, kindly proceed with the payment.`;
      sendSMS(otherUserDetails.phone, renterSmsBody);

      const ownerDetails = await getUserByID(receiverId);
      const renterName =
        otherUserDetails.firstName + "" + otherUserDetails.lastName;

      const ownerSmsBody = `Your offer for ${listingTitle} has been acccepted by ${renterName}.`;
      sendSMS(ownerDetails.phone, ownerSmsBody);

      await updateInquiryStatus(inquiryDetails.id, "Accepted");

      const chatData = chatSnapshot.val();
      const updates = {
        lastSenderId: currentUser.uid,
        messages: [...chatData.messages, messageData],
      };
      if (chatData.senderId === currentUser.uid) {
        updates.senderReadStatus = 1;
        updates.receiverReadStatus = 0;
      } else {
        updates.senderReadStatus = 0;
        updates.receiverReadStatus = 1;
      }

      await update(chatRef, updates);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleSendOffer = async () => {
    const lockedTimeConflict = getOfferLockedTimeConflict();
    if (lockedTimeConflict) {
      setError(getLockedTimeMessage(lockedTimeConflict));
      return;
    }

    setLoading(true);

    const date = new Date();
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    // const formattedDate = date.toLocaleDateString('en-US', options);
    const formattedDate = moment(`${offeredDate}`).format("dddd, MMMM D, YYYY");

    function convertTo12HourFormat(time24) {
      const [hour, minute] = time24.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";

      const hour12 = hour % 12 || 12;

      const formattedMinute = minute < 10 ? `0${minute}` : minute;

      return `${hour12}:${formattedMinute} ${period}`;
    }

    try {
      let setDuration = "";

      if (parseInt(overnight) === 1) {
        setDuration = "1 Night";
      }

      if (parseInt(overnight) > 1) {
        setDuration = `${overnight} Days`;
      }

      if (parseInt(sameDayHours) === 1) {
        setDuration = "1 Hour";
      }

      if (parseInt(sameDayHours) > 1) {
        setDuration = `${sameDayHours} Hours`;
      }
      console.log("??????????????????", offerAddresses.code.pickupCode);

      const newOffer = {
        offerPrice: calculateEstimatedPricing() || 0,
        offeredDate: formattedDate,
        offeredTime: `${offeredTime}`,
        // Save both coordinate object and string address for pickup
        pickupAddress: offerAddresses.code.pickupCode,
        pickupAddressString: offerAddresses.address.pickupAddress, // Add string address
        additionalCost: additionalCost,
        discount: ownerDiscount,
        trailerCheckbox: trailerCheckbox ? true : false,
        captainCheckbox: captainCheckbox ? true : false,
        deliveryPickup: deliveryPickupCheckbox ? true : false,
        // Save both coordinate object and string address for dropoff
        dropOffAddress: offerAddresses.code.dropOffCode,
        dropOffAddressString: offerAddresses.address.dropOffAddress, // Add string address
        offeredHours: sameDayHours,
        overnight: overnight,
        duration: setDuration,
        arivingDateTime: arivingDateTime,
      };

      await addOfferToInquiry(currentMessageInquiryId, newOffer);

      const updatedMessages = selectedChat?.messages?.map((message) => {
        if (message.inquiryId === currentMessageInquiryId) {
          // Update the offerSent property to 1
          return { ...message, offerStatus: "Sent", isUpdateOffer: false };
        }
        return message;
      });
      const updatedChat = { ...selectedChat, messages: updatedMessages };
      const chatRef = ref(database, `chats/${selectedChat.chatId}`);
      await update(chatRef, { messages: updatedChat.messages });

      setSelectedChat(updatedChat);

      await sendOfferMessage();
      setLoading(false);
      closeCreateOfferModal();
      setShowOfferSentGuidanceModal(true);
    } catch (error) {
      console.error("Error sending offer:", error);
      setLoading(false);
    }
  };
  const handlePayNowFromProposedEdit = async (message) => {
    try {
      setLoading(true);
      const database = getDatabase();
      const targetId = message.bookingId || message.inquiryId;

      if (!targetId) {
        setLoading(false);
        toast.error("Booking or Inquiry not found.");
        return;
      }

      let inquiryId = targetId;
      let bookingId = targetId;
      let inquiryData = null;
      let bookingData = null;

      // 1. First check if targetId exists directly in bookings/
      const bkgRef = ref(database, `bookings/${targetId}`);
      const bkgSnap = await get(bkgRef);
      if (bkgSnap.exists()) {
        bookingData = bkgSnap.val();
        bookingId = targetId;
        inquiryId = bookingData.inquiryId || targetId;
      }

      // 2. Also fetch/check inquiries/ if inquiryId exists
      if (inquiryId) {
        const inqRef = ref(database, `inquiries/${inquiryId}`);
        const inqSnap = await get(inqRef);
        if (inqSnap.exists()) {
          inquiryData = inqSnap.val();
        }
      }

      // If neither bookingData nor inquiryData exists, error out
      if (!bookingData && !inquiryData) {
        setLoading(false);
        toast.error("Booking details not found.");
        return;
      }

      // If we only found inquiryData, try to look up an existing booking by inquiryId
      if (!bookingData && inquiryId) {
        const bookingArr = await getBookingByInquiryId(inquiryId);
        if (bookingArr && bookingArr.length > 0) {
          bookingId = bookingArr[0]?.id;
          bookingData = bookingArr[0];
        }
      }

      const userRef = ref(database, `users/${currentUser.uid}`);
      const previousBookings = (await get(userRef)).val();
      let activeBookings;

      if (previousBookings?.activeBookings) {
        activeBookings = previousBookings.activeBookings + 1;
        await update(userRef, { activeBookings });
      } else {
        activeBookings = 1;
        await update(userRef, { activeBookings });
      }

      const renterUid = bookingData?.renter || inquiryData?.renter || currentUser.uid;
      const ownerUid = bookingData?.owner || inquiryData?.owner || message.senderId;
      const listingTitleVal = selectedChat?.listingName || bookingData?.listingTitle || inquiryData?.listingTitle || "Boat";
      const listingIdVal = bookingData?.listingId || bookingData?.listingID || inquiryData?.listingId || inquiryData?.listingID || "";
      const priceOfferedVal = parseFloat(bookingData?.priceOffered || inquiryData?.offerData?.offerPrice || message.proposedEditData?.newPrice || 0);
      const totalWaiversVal =
        toPassengerCount(
          bookingData?.totalWaivers || inquiryData?.inquiryData?.total,
          "total"
        ) || 1;

      if (bookingId && bookingData) {
        // Update the existing booking
        const existingBookingRef = ref(database, `bookings/${bookingId}`);
        const updatePayload = {
          renter: renterUid,
          owner: ownerUid,
          listingTitle: listingTitleVal,
          priceOffered: priceOfferedVal,
          listingId: listingIdVal,
          totalWaivers: totalWaiversVal,
          timestamp: serverTimestamp(),
        };

        // If booking doesn't have a paymentId yet, create one
        if (!bookingData.paymentId) {
          const newPaymentRef = push(ref(database, `payments`));
          updatePayload.paymentId = newPaymentRef.key;
        }

        await update(existingBookingRef, updatePayload);
      } else {
        // Create a new booking
        const newBookingRef = push(ref(database, `bookings`));
        bookingId = newBookingRef.key;

        const newPaymentRef = push(ref(database, `payments`));
        const newPaymentId = newPaymentRef.key;

        await set(newBookingRef, {
          renter: renterUid,
          owner: ownerUid,
          inquiryId: inquiryId,
          listingTitle: listingTitleVal,
          priceOffered: priceOfferedVal,
          listingId: listingIdVal,
          paymentId: newPaymentId,
          totalWaivers: totalWaiversVal,
          waiversFilled: { adult: 0, minor: 0 },
          timestamp: serverTimestamp(),
          status: "pending",
          ...getBookingSourceData(),
        });

        notifyNewBooking(listingTitleVal, bookingId).catch((err) =>
          console.error("Failed to create admin notification:", err)
        );
        notifyOwnerNewBooking(ownerUid, listingTitleVal, bookingId).catch((err) =>
          console.error("Failed to create owner notification:", err)
        );
      }

      // Store acceptOfferData for PaymentForm
      const acceptOfferData = {
        inquiryId: inquiryId || bookingId,
        bookingId: bookingId,
        chatId: selectedChat?.chatId || "",
        senderId: currentUser.uid,
        receiverId: message.senderId || ownerUid,
        listingId: listingIdVal,
        listingTitle: listingTitleVal,
        otherUserPhone: otherUserDetails?.phone || "",
        renterName: currentUser.displayName || currentUser.firstName || "Renter",
      };
      localStorage.setItem("acceptOfferData", JSON.stringify(acceptOfferData));

      setLoading(false);
      const queryParams = new URLSearchParams({ bookingId });
      Navigate(`/dashboard/booking-payment?${queryParams.toString()}`);
    } catch (error) {
      console.error("Error in handlePayNowFromProposedEdit:", error);
      setLoading(false);
      toast.error("Failed to proceed to payment.");
    }
  };

  const handleAcceptProposedEdit = async (message) => {
    const editKey = message.timestamp || message.bookingId;
    if (acceptingEditKey === editKey) return;
    setAcceptingEditKey(editKey);

    try {
      const bookingRef = ref(database, `bookings/${message.bookingId}/proposedEdit`);
      const bookingSnapshot = await get(bookingRef);
      if (!bookingSnapshot.exists()) {
        toast.error("Proposed edit not found or already processed.");
        setAcceptingEditKey(null);
        return;
      }
      const proposedEdit = bookingSnapshot.val();

      if (proposedEdit.status && proposedEdit.status !== 'pending') {
        toast.error("Proposed edit has already been processed.");
        setAcceptingEditKey(null);
        return;
      }

      if (proposedEdit.requiresStripeCharge) {
        const mainBookingRef = ref(database, `bookings/${message.bookingId}`);
        const mainBookingSnapshot = await get(mainBookingRef);
        const bookingData = mainBookingSnapshot.val();
        
        let customerId = bookingData.renterStripeCustomerId;
        let paymentIntentId = null;
        let allChargeIds = [];
        let currentPaymentDetails = null;
        if (bookingData.paymentId) {
            const paymentRef = ref(database, `payments/${bookingData.paymentId}`);
            const paymentSnapshot = await get(paymentRef);
            if (paymentSnapshot.exists()) {
                currentPaymentDetails = paymentSnapshot.val();
                customerId = customerId || currentPaymentDetails.customerId;
                paymentIntentId = currentPaymentDetails.stripePaymentIntentId || currentPaymentDetails.paymentIntentId || currentPaymentDetails.finalChargeIntentId || currentPaymentDetails.transactionId;
                
                if (paymentIntentId) allChargeIds.push(paymentIntentId);
                if (currentPaymentDetails.additionalCharges) {
                    allChargeIds = [...allChargeIds, ...Object.keys(currentPaymentDetails.additionalCharges)];
                }
            }
        }

        const baseUrl = process.env.REACT_APP_BASE_URL || '';
        const apiUrl = `${baseUrl}/api/edit-booking-payment`;
            
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                customerId: customerId,
                paymentIntentId: paymentIntentId,
                allChargeIds: allChargeIds,
                bookingId: message.bookingId,
                amountDifference: proposedEdit.stripeChargeAmountCents,
                description: `Edit Booking - ${selectedChat?.listingName || bookingData.listingTitle || bookingData.boatName || 'Boat'} - ${selectedChat?.renterName || 'Renter'}`
            })
        });
        
        const result = await response.json();
        if (!response.ok || !result.success) {
            toast.error(result.error || "Stripe transaction failed. Please update your payment method.");
            return;
        }
        toast.success(`Stripe ${proposedEdit.stripeChargeAmountCents > 0 ? 'charge' : 'refund'} successful!`);
        
        if (bookingData.paymentId && proposedEdit.stripeChargeAmountCents !== 0) {
            let parsedUpdates = {};
            try {
                parsedUpdates = typeof proposedEdit.firebaseUpdates === 'string' ? JSON.parse(proposedEdit.firebaseUpdates) : (proposedEdit.firebaseUpdates || {});
            } catch(e) { console.error(e); }
            
            let currentPaid = currentPaymentDetails?.paidAmount || 0;
            parsedUpdates[`payments/${bookingData.paymentId}/paidAmount`] = currentPaid + proposedEdit.stripeChargeAmountCents;
            
            if (proposedEdit.stripeChargeAmountCents > 0 && result.stripeTransactionId) {
                parsedUpdates[`payments/${bookingData.paymentId}/additionalCharges/${result.stripeTransactionId}`] = {
                    amount: proposedEdit.stripeChargeAmountCents,
                    timestamp: new Date().toISOString()
                };
            }
            proposedEdit.firebaseUpdates = parsedUpdates;
        }
      }

      let firebaseUpdatesObj = {};
      try {
        firebaseUpdatesObj = typeof proposedEdit.firebaseUpdates === 'string' 
            ? JSON.parse(proposedEdit.firebaseUpdates) 
            : (proposedEdit.firebaseUpdates || {});
      } catch(e) {
        console.error("Failed to parse firebaseUpdates", e);
      }
      const mainUpdates = { ...firebaseUpdatesObj };
      mainUpdates[`bookings/${message.bookingId}/proposedEdit`] = null; 

      const updatedMessages = selectedChat?.messages?.map((m) => {
        if (m.timestamp === message.timestamp && m.type === "proposedEdit") {
          return { ...m, status: "accepted" };
        }
        return m;
      }) || [];

      const notificationMsg = {
        message: "The renter has approved the proposed changes to the booking.",
        senderId: currentUser.uid,
        receiverId: message.senderId,
        timestamp: new Date().toISOString(),
        type: "text",
        isUnread: true
      };
      
      const newMessagesArray = [...updatedMessages, notificationMsg];
      mainUpdates[`chats/${selectedChat.chatId}/messages`] = newMessagesArray;
      mainUpdates[`chats/${selectedChat.chatId}/lastMessage`] = notificationMsg.message;
      mainUpdates[`chats/${selectedChat.chatId}/lastMessageTime`] = notificationMsg.timestamp;
      mainUpdates[`chats/${selectedChat.chatId}/lastSenderId`] = currentUser.uid;
      mainUpdates[`chats/${selectedChat.chatId}/receiverReadStatus`] = 0;
      mainUpdates[`chats/${selectedChat.chatId}/senderReadStatus`] = 1;

      await update(ref(database), mainUpdates);
      
      const updatedChat = { ...selectedChat, messages: newMessagesArray };
      setSelectedChat(updatedChat);
      
      toast.success("Booking successfully updated!");

      if (!proposedEdit.requiresStripeCharge && currentUser?.uid === message.receiverId && !isBookingPaid) {
        setPayNowModalMessage(message);
        setShowPayNowModal(true);
      }

      // --- NEW NOTIFICATION CODE ---
      try {
        const ownerData = await getUserByID(message.senderId);
        const renterName = currentUser?.firstName && currentUser?.lastName ? `${currentUser.firstName} ${currentUser.lastName}` : "The renter";
        const listingTitle = selectedChat?.listingName || "their booking";

        const ownerSmsBody = `${renterName} has accepted your proposed changes for ${listingTitle}.`;
        if (ownerData?.phone) sendSMS(ownerData.phone, ownerSmsBody);

        const emailSubject = "Booking Edit Accepted";
        const emailBody = `
            <div style="font-family: Arial, sans-serif; color: #333;">
                <h2>Booking Update Accepted</h2>
                <p>Hi ${ownerData?.firstName || 'Owner'},</p>
                <p><strong>${renterName}</strong> has accepted your proposed changes for <strong>${listingTitle}</strong>.</p>
                <p>The booking details have been successfully updated.</p>
                <br/>
                <a href="${process.env.REACT_APP_BASE_URL}/dashboard/inbox" style="padding: 10px 20px; background-color: #007bff; color: white; text-decoration: none; border-radius: 5px;">View Inbox</a>
                <br/><br/>
                <p>Thank you,<br/>BBR Support Team</p>
            </div>
        `;
        if (ownerData?.email) sendEmail(ownerData.email, emailSubject, emailBody);

        fetch(`${process.env.REACT_APP_BASE_URL}/schedule-message`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                senderId: currentUser.uid,
                receiverId: message.senderId,
                messageText: notificationMsg.message,
                chatId: selectedChat.chatId,
                timestamp: notificationMsg.timestamp,
                lastSenderId: currentUser.uid
            }),
        }).catch(e => console.error("Notification failed", e));
      } catch (notificationError) {
          console.error("Error sending SMS/Email/Push notifications:", notificationError);
      }
      // --- END NEW NOTIFICATION CODE ---

    } catch (e) {
      console.error(e);
      toast.error("Failed to accept the proposed edit.");
    }
  };

  const handleDeclineProposedEdit = async (message) => {
    try {
      const mainUpdates = {};
      mainUpdates[`bookings/${message.bookingId}/proposedEdit`] = null;

      const updatedMessages = selectedChat?.messages?.map((m) => {
        if (m.timestamp === message.timestamp && m.type === "proposedEdit") {
          return { ...m, status: "declined" };
        }
        return m;
      }) || [];

      const notificationMsg = {
        message: "The renter has declined the proposed changes to the booking.",
        senderId: currentUser.uid,
        receiverId: message.senderId,
        timestamp: new Date().toISOString(),
        type: "text",
        isUnread: true
      };
      
      const newMessagesArray = [...updatedMessages, notificationMsg];
      mainUpdates[`chats/${selectedChat.chatId}/messages`] = newMessagesArray;
      mainUpdates[`chats/${selectedChat.chatId}/lastMessage`] = notificationMsg.message;
      mainUpdates[`chats/${selectedChat.chatId}/lastMessageTime`] = notificationMsg.timestamp;
      mainUpdates[`chats/${selectedChat.chatId}/lastSenderId`] = currentUser.uid;
      mainUpdates[`chats/${selectedChat.chatId}/receiverReadStatus`] = 0;
      mainUpdates[`chats/${selectedChat.chatId}/senderReadStatus`] = 1;

      await update(ref(database), mainUpdates);
      
      const updatedChat = { ...selectedChat, messages: newMessagesArray };
      setSelectedChat(updatedChat);
      
      toast.success("Proposed edit declined.");

      // --- NEW NOTIFICATION CODE ---
      try {
        const ownerData = await getUserByID(message.senderId);
        const renterName = currentUser?.firstName && currentUser?.lastName ? `${currentUser.firstName} ${currentUser.lastName}` : "The renter";
        const listingTitle = selectedChat?.listingName || "their booking";

        const ownerSmsBody = `${renterName} has declined your proposed changes for ${listingTitle}.`;
        if (ownerData?.phone) sendSMS(ownerData.phone, ownerSmsBody);

        const emailSubject = "Booking Edit Declined";
        const emailBody = `
            <div style="font-family: Arial, sans-serif; color: #333;">
                <h2>Booking Update Declined</h2>
                <p>Hi ${ownerData?.firstName || 'Owner'},</p>
                <p><strong>${renterName}</strong> has declined your proposed changes for <strong>${listingTitle}</strong>.</p>
                <p>The booking will proceed with the original details unless you propose another change.</p>
                <br/>
                <a href="${process.env.REACT_APP_BASE_URL}/dashboard/inbox" style="padding: 10px 20px; background-color: #007bff; color: white; text-decoration: none; border-radius: 5px;">View Inbox</a>
                <br/><br/>
                <p>Thank you,<br/>BBR Support Team</p>
            </div>
        `;
        if (ownerData?.email) sendEmail(ownerData.email, emailSubject, emailBody);

        fetch(`${process.env.REACT_APP_BASE_URL}/schedule-message`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                senderId: currentUser.uid,
                receiverId: message.senderId,
                messageText: notificationMsg.message,
                chatId: selectedChat.chatId,
                timestamp: notificationMsg.timestamp,
                lastSenderId: currentUser.uid
            }),
        }).catch(e => console.error("Notification failed", e));
      } catch (notificationError) {
          console.error("Error sending SMS/Email/Push notifications:", notificationError);
      }
      // --- END NEW NOTIFICATION CODE ---

    } catch (e) {
      console.error(e);
      toast.error("Failed to decline the proposed edit.");
    }
  };

  const handleWithdrawProposedEdit = async (message) => {
    try {
      const mainUpdates = {};
      mainUpdates[`bookings/${message.bookingId}/proposedEdit`] = null;

      const updatedMessages = selectedChat?.messages?.map((m) => {
        if (m.timestamp === message.timestamp && m.type === "proposedEdit") {
          return { ...m, status: "withdrawn" };
        }
        return m;
      });
      mainUpdates[`chats/${selectedChat.chatId}/messages`] = updatedMessages;

      const notificationMsg = {
        message: "The boat owner has withdrawn the proposed changes to the booking.",
        senderId: currentUser.uid,
        receiverId: message.receiverId,
        timestamp: new Date().toISOString(),
        type: "text",
        isUnread: true
      };
      
      const newMessagesArray = [...updatedMessages, notificationMsg];
      mainUpdates[`chats/${selectedChat.chatId}/messages`] = newMessagesArray;
      mainUpdates[`chats/${selectedChat.chatId}/lastMessage`] = notificationMsg.message;
      mainUpdates[`chats/${selectedChat.chatId}/lastMessageTime`] = notificationMsg.timestamp;
      mainUpdates[`chats/${selectedChat.chatId}/lastSenderId`] = currentUser.uid;
      mainUpdates[`chats/${selectedChat.chatId}/receiverReadStatus`] = 0;
      mainUpdates[`chats/${selectedChat.chatId}/senderReadStatus`] = 1;

      await update(ref(database), mainUpdates);
      
      const updatedChat = { ...selectedChat, messages: newMessagesArray };
      setSelectedChat(updatedChat);
      
      toast.success("Proposed edit withdrawn.");

      // --- NEW NOTIFICATION CODE ---
      try {
        const renterData = await getUserByID(message.receiverId);
        const ownerName = currentUser?.firstName && currentUser?.lastName ? `${currentUser.firstName} ${currentUser.lastName}` : "The boat owner";
        const listingTitle = selectedChat?.listingName || "your booking";

        const renterSmsBody = `${ownerName} has withdrawn the proposed changes for ${listingTitle}.`;
        if (renterData?.phone) sendSMS(renterData.phone, renterSmsBody);

        const emailSubject = "Booking Edit Withdrawn";
        const emailBody = `
            <div style="font-family: Arial, sans-serif; color: #333;">
                <h2>Booking Update Withdrawn</h2>
                <p>Hi ${renterData?.firstName || 'Renter'},</p>
                <p><strong>${ownerName}</strong> has withdrawn the proposed changes for <strong>${listingTitle}</strong>.</p>
                <p>Your booking remains unchanged.</p>
                <br/>
                <a href="${process.env.REACT_APP_BASE_URL}/dashboard/inbox" style="padding: 10px 20px; background-color: #007bff; color: white; text-decoration: none; border-radius: 5px;">View Inbox</a>
                <br/><br/>
                <p>Thank you,<br/>BBR Support Team</p>
            </div>
        `;
        if (renterData?.email) sendEmail(renterData.email, emailSubject, emailBody);

        fetch(`${process.env.REACT_APP_BASE_URL}/schedule-message`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                senderId: currentUser.uid,
                receiverId: message.receiverId,
                messageText: notificationMsg.message,
                chatId: selectedChat.chatId,
                timestamp: notificationMsg.timestamp,
                lastSenderId: currentUser.uid
            }),
        }).catch(e => console.error("Notification failed", e));
      } catch (notificationError) {
          console.error("Error sending SMS/Email/Push notifications:", notificationError);
      }
      // --- END NEW NOTIFICATION CODE ---

    } catch (e) {
      console.error(e);
      toast.error("Failed to withdraw the proposed edit.");
    }
  };

  const handleEditOffer = async () => {
    const lockedTimeConflict = getOfferLockedTimeConflict();
    if (lockedTimeConflict) {
      setError(getLockedTimeMessage(lockedTimeConflict));
      return;
    }

    setLoading(true);

    const date = new Date(`${offeredDate}`);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    // const formattedDate = date.toLocaleDateString('en-US', options);
    const formattedDate = moment(`${offeredDate}`).format("dddd, MMMM D, YYYY");

    function convertTo12HourFormat(time24) {
      const [hour, minute] = time24.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";

      const hour12 = hour % 12 || 12;

      const formattedMinute = minute < 10 ? `0${minute}` : minute;

      return `${hour12}:${formattedMinute} ${period}`;
    }

    try {
      const offerDate = offeredDate;
      const offerTime = offeredTime;

      let setDuration = "";

      if (parseInt(overnight) === 1) {
        setDuration = "1 Night";
      }

      if (parseInt(overnight) > 1) {
        setDuration = `${overnight} Days`;
      }

      if (parseInt(sameDayHours) === 1) {
        setDuration = "1 Hour";
      }

      if (parseInt(sameDayHours) > 1) {
        setDuration = `${sameDayHours} Hours`;
      }
      // offeredDateofferedDateofferedDate

      console.log(
        "`${convertTo12HourFormat(offerTime)}`",
        `${convertTo12HourFormat(offerTime)}`
      );

      const newOffer = {
        offerPrice: calculateEstimatedPricing() || 0,
        offeredDate: `${formattedDate}`,
        offeredTime: `${offeredTime}`,
        // Save both coordinate object and string address for pickup
        pickupAddress: offerAddresses?.code?.pickupCode,
        pickupAddressString: offerAddresses?.address?.pickupAddress, // Add string address
        additionalCost: additionalCost ?? 0,
        discount: ownerDiscount ?? 0,
        trailerCheckbox: trailerCheckbox ? true : false,
        captainCheckbox: captainCheckbox ? true : false,
        deliveryPickup: deliveryPickupCheckbox ? true : false,
        // Save both coordinate object and string address for dropoff
        dropOffAddress: offerAddresses?.code?.dropOffCode,
        dropOffAddressString: offerAddresses?.address?.dropOffAddress, // Add string address
        offeredHours: sameDayHours,
        overnight: overnight || 0,
        duration: setDuration,
        arivingDateTime: arivingDateTime,
        isUpdateOffer: true,
      };
      console.log("newOffer``````````---0-0-0-0-0-00-", newOffer);
      let editOffer = await editOfferToInquiry(
        currentMessageInquiryId,
        newOffer
      );
      console.log("editOffer badlooooooo", editOffer);
      // Find the message in the selected chat that corresponds to the inquiry ID
      const updatedMessages = selectedChat?.messages?.map((message) => {
        if (message?.inquiryId === currentMessageInquiryId) {
          console.log("updatedMessages 1111111", message);
          // Update the offerSent property to 1
          return {
            ...message,
            offerStatus: "Sent",
            disableViewOfferBTN: true,
            ...newOffer,
          };
        }
        return message;
      });

      // Update the chat with the modified messages
      const updatedChat = { ...selectedChat, messages: updatedMessages };

      // Update the chat in the Firebase Realtime Database
      const chatRef = ref(database, `chats/${selectedChat.chatId}`);
      await update(chatRef, { messages: updatedChat.messages });

      // Optionally, you can update the state to reflect the changes
      setSelectedChat(updatedChat);

      await sendOfferMessage();
      setLoading(false);
      closeEditOfferModal();
    } catch (error) {
      console.error("Error editing offer:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (parseInt(sameDayHours) > 0) {
      setOvernight(0);
    }
    if (parseInt(overnight) > 0) {
      setSameDayHours(0);
    }
  }, [sameDayHours, overnight]);

  async function handleAcceptOffer() {
    setLoading(true);
    const database = getDatabase();
    const bookingRef = ref(database, `bookings`);
    const paymentRef = ref(database, `payments`);

    try {
      const booking = await getBookingByInquiryId(inquiryDetails?.id);

      let bookingId;
      if (booking) {
        bookingId = booking[0]?.id;
      }

      const userRef = ref(database, `users/${currentUser.uid}`);
      const previousBookings = (await get(userRef)).val();
      let activeBookings;

      if (previousBookings?.activeBookings) {
        activeBookings = previousBookings.activeBookings + 1;
        await update(userRef, { activeBookings });
      } else {
        activeBookings = 1;
        await update(userRef, { activeBookings });
      }
      if (bookingId) {
        // Update the existing booking
        const existingBookingRef = ref(database, `bookings/${bookingId}`);
        await update(existingBookingRef, {
          renter: inquiryDetails.renter,
          owner: inquiryDetails.owner,
          listingTitle: listingTitle,
          priceOffered: parseFloat(inquiryDetails?.offerData?.offerPrice),
          listingId: inquiryDetails.listingId || inquiryDetails.listingID,
          totalWaivers: inquiryDetails.inquiryData.total,
          waiversFilled: {
            adult: 0,
            minor: 0,
          },
          timestamp: serverTimestamp(),
          // status: "pending",
        });
      } else {
        // Create a new booking if no existing booking is found
        const newBookingRef = push(bookingRef);
        bookingId = newBookingRef.key;

        const newPaymentRef = push(paymentRef);
        const newPaymentId = newPaymentRef.key;

        await set(newBookingRef, {
          renter: inquiryDetails.renter,
          owner: inquiryDetails.owner,
          inquiryId: currentMessageInquiryId,
          listingTitle: listingTitle,
          priceOffered: parseInt(inquiryDetails.offerData.offerPrice),
          listingId: inquiryDetails.listingId || inquiryDetails.listingID,
          paymentId: newPaymentId,
          totalWaivers: inquiryDetails.inquiryData.total,
          waiversFilled: {
            adult: 0,
            minor: 0,
          },
          timestamp: serverTimestamp(),
          status: "pending",
          ...getBookingSourceData(),
        });

        notifyNewBooking(listingTitle, bookingId).catch((err) =>
          console.error("Failed to create admin notification:", err)
        );
      }

      const renterData = await getCurrentUserDetails();
      const renterName = [renterData?.firstName, renterData?.lastName]
        .filter(Boolean)
        .join(" ");

      notifyOwnerOfferAccepted(
        inquiryDetails.owner,
        listingTitle,
        bookingId,
        renterName
      ).catch((err) =>
        console.error("Failed to create owner notification:", err)
      );

      // Update messages in the selected chat
      const updatedMessages = selectedChat?.messages?.map((message) => {
        if (message.inquiryId === currentMessageInquiryId) {
          return { ...message, offerStatus: "Accepted" };
        }
        return message;
      });

      const updatedChat = { ...selectedChat, messages: updatedMessages };
      const chatRef = ref(database, `chats/${selectedChat.chatId}`);
      await update(chatRef, { messages: updatedChat.messages });

      setSelectedChat(updatedChat);

      // Store the necessary data for sendAcceptOfferMessage in localStorage
      const acceptOfferData = {
        inquiryId: inquiryDetails.id,
        chatId: selectedChat.chatId,
        senderId: currentUser.uid,
        receiverId: receiverId,
        listingId: inquiryDetails.listingId,
        listingTitle: listingTitle,
        otherUserPhone: otherUserDetails?.phone,
        renterName:
          otherUserDetails?.firstName + " " + otherUserDetails?.lastName,
      };
      localStorage.setItem("acceptOfferData", JSON.stringify(acceptOfferData));

      setLoading(false);
      closeCreateOfferModal();
      const queryParams = new URLSearchParams({ bookingId });
      Navigate(`/dashboard/booking-payment?${queryParams.toString()}`);
      return bookingId;
    } catch (error) {
      console.error("Error adding or updating booking:", error);
      setLoading(false);
      throw error;
    }
  }

  useEffect(() => {
    // Calculate total duration
    const totalMinutes =
      sameDayHours * 60 + sameDayMinutes + overnight * 24 * 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    // setOfferedDuration(`${hours} hours ${minutes} minutes`)
    if (inquiryDetails?.inquiryData?.sameDayHours > 0) {
      setOfferedDuration(`${inquiryDetails?.inquiryData?.sameDayHours} hours`);
    }
    if (inquiryDetails?.inquiryData?.overnight > 0) {
      setOfferedDuration(`${inquiryDetails?.inquiryData?.overnight} nights`);
    }

    if (offeredDate && offeredTime) {
      setArivingDateTime(formatDateTimeDisplay(offeredDate, offeredTime));
    }
  }, [
    offeredDate,
    offeredTime,
    sameDayHours,
    sameDayMinutes,
    overnight,
    inquiryDetails,
  ]);

  useEffect(() => {
    if (overnight && overnight > 0) {
      setOfferedDuration(`${overnight} nights`);
      // setTrailerCheckbox(true);
    }
  }, [overnight]);

  const formatOfferDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [listingLockDates, setListingLockDates] = useState([]);
  const [disabledDates, setDisabledDates] = useState([]);

  const getOfferLockedTimeConflict = () => {
    return getLockedTimeConflict(
      listingLockDates,
      offeredDate,
      offeredTime,
      sameDayHours,
      sameDayMinutes,
      overnight
    );
  };

  const getLockedTimeMessage = (conflict) => {
    return `This listing is locked on ${dayjs(conflict.date).format("MMMM D, YYYY")} from ${conflict.start} to ${conflict.end}. Please choose another time.`;
  };

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const filterLockedDates = await getEventsByListingId(
          inquiryDetails.listingId
        );
        const disabledDatesLocal = getFullDayLockedDates(listingLockDates, "YYYY-MM-DD");

        const disabledDatesBooked = filterLockedDates?.reduce((dates, item) => {
          // Skip hourly (same-day) bookings — boat can be booked multiple times per day
          if (item.isOvernight === false) return dates;

          const datesInRange = getDatesInRange(
            moment(item?.start).startOf("day"),
            moment(item.end).startOf("day")
          );
          return [...dates, ...datesInRange];
        }, []);

        setDisabledDates([...disabledDatesLocal, ...disabledDatesBooked]);
      } catch (e) { }
    };
    fetchDates();
  }, [listingLockDates, inquiryDetails]);

  useEffect(() => {
    const fetchLockDates = async () => {
      try {
        const lockDates = await getListingLockDatesByID(
          inquiryDetails?.listingId
        );
        if (!lockDates || typeof lockDates !== "object") {
          setListingLockDates([]);
          return;
        }
        const lockDatesArray = Object.entries(lockDates).map(([id, date]) => ({
          id,
          start_date: moment(date.start_date).format("YYYY-MM-DD"),
          end_date: moment(date.end_date).format("YYYY-MM-DD"),
          time_ranges: date.time_ranges || [],
          start_time: date.start_time || null,
          end_time: date.end_time || null,
        }));
        setListingLockDates(lockDatesArray);
      } catch (error) {
        console.error("Error fetching lock dates:", error);
      }
    };
    if (inquiryDetails?.listingId) {
      fetchLockDates();
    }
  }, [inquiryDetails]);

  useEffect(() => {
    const fetchBookingStatus = async () => {
      if (!selectedChat) {
        setCurrentBookingStatus(null);
        return;
      }
      try {
        const database = getDatabase();
        const editMsg = selectedChat?.messages?.find(m => m.bookingId);
        const targetBookingId = editMsg?.bookingId || selectedChat?.inquiryDetails?.id || inquiryDetails?.id;
        
        if (!targetBookingId) {
          setCurrentBookingStatus(null);
          return;
        }

        const bkgRef = ref(database, `bookings/${targetBookingId}`);
        const bkgSnap = await get(bkgRef);
        if (bkgSnap.exists()) {
          setCurrentBookingStatus(bkgSnap.val()?.status || null);
          return;
        }

        const bookingArr = await getBookingByInquiryId(targetBookingId);
        if (bookingArr && bookingArr.length > 0) {
          setCurrentBookingStatus(bookingArr[0]?.status || null);
        } else {
          setCurrentBookingStatus(null);
        }
      } catch (err) {
        console.error("Error fetching booking status for selectedChat:", err);
      }
    };
    fetchBookingStatus();
  }, [selectedChat, inquiryDetails]);

  const isBookingPaid = Boolean(
    currentBookingStatus && 
    currentBookingStatus !== "pending" && 
    currentBookingStatus !== "unpaid"
  );

  function getDatesInRange(startDate, endDate) {
    const dates = [];
    let currentDate = moment(startDate).startOf("day"); // Start of the day
    const end = moment(endDate).startOf("day"); // End of the day
    while (currentDate.isSameOrBefore(end, "day")) {
      dates.push(currentDate.format("YYYY-MM-DD")); // Store as YYYY-MM-DD
      currentDate.add(1, "day");
    }
    return dates;
  }

  // Calculate visible messages for infinite scroll
  const allMessages = selectedChat?.messages || [];
  const visibleMessages = allMessages.slice(
    Math.max(allMessages.length - visibleLimit, 0)
  );

  return (
    <>
      <div class="row">
        <div class="col">
          <div class="page-description">
            <h1>Inbox</h1>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="mailbox-container">
            <div class="card">

              <div class="container-fluid">
                <div className="row">
                  <div className="mailbox-list col-xl-3" style={{ overflow: 'hidden' }}>

                    {currentUserDetails?.role === "Admin" && (
                      <div className="mailbox-search p-3 col-12 ">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name or email..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{
                            paddingLeft: "15px",
                            border: "1px solid #e0e0e0",
                          }}
                        />
                      </div>
                    )}

                    {currentUserDetails?.role === "Admin" && (
                      <h5 className="p-3">All Users</h5>
                    )}
                    <ul
                      className="mailbox-list"
                      onScroll={handleSidebarScroll}
                      style={{ overflowY: 'auto', maxHeight: '70vh' }}
                    >
                      {(() => {
                        if (currentUserDetails?.role === "Admin") {
                          let displayUsers = [];

                          if (searchTerm.length > 2) {
                            displayUsers = searchResults;
                          } else {
                            // Combine allUsers and active chat participants
                            const combinedUsersMap = new Map();
                            
                            allUsers.forEach(user => {
                              combinedUsersMap.set(user.uid, user);
                            });
                            
                            Object.keys(otherParticipants).forEach(uid => {
                              if (!combinedUsersMap.has(uid) && otherParticipants[uid]) {
                                combinedUsersMap.set(uid, { ...otherParticipants[uid], uid });
                              }
                            });

                            displayUsers = Array.from(combinedUsersMap.values()).filter(user => {
                              if (!searchTerm.trim()) return true;
                              const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
                              const email = (user.email || "").toLowerCase();
                              return fullName.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase());
                            });
                          }

                          const searchedUsers = displayUsers.sort((a, b) => {
                            const getChatTimestamp = (userId) => {
                              const chat = chats.find(c =>
                                (c.senderId === currentUser.uid && c.receiverId === userId) ||
                                (c.senderId === userId && c.receiverId === currentUser.uid)
                              );
                              return chat ? getTimestamp(chat) : 0;
                            };
                            return getChatTimestamp(b.uid) - getChatTimestamp(a.uid);
                          });

                          if (searchedUsers.length === 0) {
                            return (
                              <li className="mailbox-list-item text-center p-3">
                                <span className="text-muted">{isSearching ? "Searching..." : "No users found"}</span>
                              </li>
                            );
                          }

                          return searchedUsers.map((user) => {
                            const isActive = receiverId === user.uid;
                            const chat = chats.find(c =>
                              (c.senderId === currentUser.uid && c.receiverId === user.uid) ||
                              (c.senderId === user.uid && c.receiverId === currentUser.uid)
                            );
                            const lastMessageTimestamp = chat ? getTimestamp(chat) : 0;
                            const formattedDate = lastMessageTimestamp
                              ? `${new Date(lastMessageTimestamp).toLocaleDateString()} ${new Date(lastMessageTimestamp).toLocaleTimeString()}`
                              : "";

                            return (
                              <li
                                key={user.uid}
                                className={`mailbox-list-item ${isActive ? "active" : ""}`}
                                onClick={() => selectContact(user.uid)}
                                style={{
                                  borderLeft: isActive ? "4px solid #007bff" : "none",
                                  backgroundColor: isActive ? "#e8f4fd" : "transparent",
                                }}
                              >
                                <a href="javascript:void(0)">
                                  <div className="mailbox-list-item-content">
                                    <span className="mailbox-list-item-title">
                                      <div className="user-mailbox-wrapper">
                                        {`${user.firstName} ${user.lastName}`}
                                        {(() => {
                                          const isUnread = chat && chat.lastSenderId && chat.lastSenderId !== currentUser.uid &&
                                            ((chat.senderId === currentUser.uid && chat.senderReadStatus === 0) ||
                                              (chat.receiverId === currentUser.uid && chat.receiverReadStatus === 0));

                                          if (!isUnread) return null;

                                          let unreadMsgCount = 0;
                                          if (chat.messages && chat.messages.length > 0) {
                                            for (let i = chat.messages.length - 1; i >= 0; i--) {
                                              if (chat.messages[i].senderId !== currentUser.uid) {
                                                unreadMsgCount++;
                                              } else {
                                                break;
                                              }
                                            }
                                          }

                                          return unreadMsgCount > 0 ? (
                                            <span style={{
                                              backgroundColor: '#ff4d4f',
                                              color: '#fff',
                                              borderRadius: '50%',
                                              minWidth: '18px',
                                              height: '18px',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontSize: '11px',
                                              fontWeight: 'bold',
                                              marginLeft: '8px',
                                              padding: '0 4px'
                                            }}>
                                              {unreadMsgCount}
                                            </span>
                                          ) : null;
                                        })()}
                                        <BoatTypeVerifiedBadge
                                          user={user}
                                          userMeta={usersMetaById[user.uid]}
                                        />
                                      </div>
                                    </span>
                                    <p className="mailbox-list-item-text">
                                      {user.email}
                                    </p>
                                    {formattedDate && (
                                      <p className="mailbox-list-item-text" style={{ fontSize: '11px', color: '#888' }}>
                                        Last Message: {formattedDate}
                                      </p>
                                    )}
                                  </div>
                                </a>
                              </li>
                            );
                          });
                        }

                        const filteredChats = chats
                          .filter((chat) => {
                            const isParticipant =
                              currentUser.uid === chat.senderId ||
                              currentUser.uid === chat.receiverId;

                            if (currentUserDetails?.role === "Admin") {
                              if (!searchTerm.trim()) return true;
                              // Admin global search logic...
                            }

                            if (!isParticipant) return false;
                            if (!searchTerm.trim()) return true;

                            const otherParticipantId =
                              currentUser.uid === chat.senderId
                                ? chat.receiverId
                                : chat.senderId;
                            const details = otherParticipants[otherParticipantId];
                            const nameMatch = details
                              ? `${details.firstName} ${details.lastName}`
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase())
                              : false;

                            const messageMatch = chat.messages?.some((msg) =>
                              msg.message
                                ?.toLowerCase()
                                .includes(searchTerm.toLowerCase())
                            );

                            return nameMatch || messageMatch;
                          })
                          .sort((a, b) => getTimestamp(b) - getTimestamp(a));

                        if (filteredChats.length === 0) {
                          return (
                            <li className="mailbox-list-item text-center p-3">
                              <span className="text-muted">No results found</span>
                            </li>
                          );
                        }

                        return filteredChats.map((chat) => {
                          const isParticipant = currentUser.uid === chat.senderId || currentUser.uid === chat.receiverId;

                          const otherParticipantId =
                            currentUser.uid === chat.senderId
                              ? chat.receiverId
                              : chat.senderId;
                          const otherParticipantDetails =
                            otherParticipants[otherParticipantId];

                          const senderDetails = otherParticipants[chat.senderId];
                          const receiverDetails = otherParticipants[chat.receiverId];

                          const lastMessageTimestamp = getTimestamp(chat);
                          const lastMessageDate = new Date(lastMessageTimestamp);

                          const formattedDate = `${lastMessageDate.toLocaleDateString()} ${lastMessageDate.toLocaleTimeString()}`;

                          const isActive = receiverId === otherParticipantId ||
                            (currentUserDetails?.role === "Admin" && selectedChat?.chatId === chat.chatId);

                          return (
                            <li
                              key={chat.chatId}
                              className={`mailbox-list-item ${isActive ? "active" : ""
                                }`}
                              style={{
                                borderLeft:
                                  isActive
                                    ? "4px solid #007bff"
                                    : "none",
                                backgroundColor:
                                  isActive
                                    ? "#e8f4fd"
                                    : "transparent",
                              }}
                              onClick={() => {
                                if (currentUserDetails?.role === "Admin" && !isParticipant) {
                                  setSelectedChat(chat);
                                  setReceiverId(""); // Clear receiverId if searching as admin for third party chats
                                } else {
                                  selectContact(otherParticipantId);
                                }
                              }}
                            >
                              <a href="javascript:void(0)">
                                <div className="mailbox-list-item-content">
                                  <span className=" mailbox-list-item-title">
                                    {currentUserDetails?.role === "Admin" ? (
                                      <div className="user-mailbox-wrapper">
                                        {senderDetails && receiverDetails ? (
                                          `${senderDetails.firstName} ${senderDetails.lastName} & ${receiverDetails.firstName} ${receiverDetails.lastName}`
                                        ) : (
                                          "Loading participants..."
                                        )}
                                      </div>
                                    ) : otherParticipantDetails ? (
                                      <div className="user-mailbox-wrapper d-flex align-items-center">
                                        <div className="d-flex align-items-center">
                                          <span style={{
                                            fontWeight: (
                                              isParticipant && chat.lastSenderId && chat.lastSenderId !== currentUser.uid &&
                                              ((chat.senderId === currentUser.uid && chat.senderReadStatus === 0) ||
                                                (chat.receiverId === currentUser.uid && chat.receiverReadStatus === 0))
                                            ) ? "700" : "500",
                                            color: (
                                              isParticipant && chat.lastSenderId && chat.lastSenderId !== currentUser.uid &&
                                              ((chat.senderId === currentUser.uid && chat.senderReadStatus === 0) ||
                                                (chat.receiverId === currentUser.uid && chat.receiverReadStatus === 0))
                                            ) ? "#000" : "inherit"
                                          }}>
                                            {`${otherParticipantDetails.firstName} ${otherParticipantDetails.lastName} `}
                                          </span>
                                          {(() => {
                                            const isUnread = isParticipant && chat.lastSenderId && chat.lastSenderId !== currentUser.uid &&
                                              ((chat.senderId === currentUser.uid && chat.senderReadStatus === 0) ||
                                                (chat.receiverId === currentUser.uid && chat.receiverReadStatus === 0));

                                            if (!isUnread) return null;

                                            let unreadMsgCount = 0;
                                            if (chat.messages && chat.messages.length > 0) {
                                              for (let i = chat.messages.length - 1; i >= 0; i--) {
                                                if (chat.messages[i].senderId !== currentUser.uid) {
                                                  unreadMsgCount++;
                                                } else {
                                                  break;
                                                }
                                              }
                                            }

                                            return unreadMsgCount > 0 ? (
                                              <span style={{
                                                backgroundColor: '#ff4d4f',
                                                color: '#fff',
                                                borderRadius: '50%',
                                                minWidth: '18px',
                                                height: '18px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                marginLeft: '8px',
                                                padding: '0 4px'
                                              }}>
                                                {unreadMsgCount}
                                              </span>
                                            ) : null;
                                          })()}
                                        </div>
                                        <BoatTypeVerifiedBadge
                                          user={otherParticipantDetails}
                                          userMeta={otherParticipantDetails?._userMeta}
                                        />
                                      </div>
                                    ) : (
                                      "Loading..."
                                    )}
                                  </span>
                                  <p className="mailbox-list-item-text">
                                    Last Message: {formattedDate}{" "}
                                    {/* Display the timestamp */}
                                  </p>
                                  <p className="mailbox-list-item-text">
                                    {/* Add your text content or other details here */}
                                  </p>
                                </div>
                              </a>
                            </li>
                          );
                        });
                      })()}
                    </ul>
                  </div>
                  <div class="mailbox-open-content col-xl-9" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    {(selectedChat || (receiverId && otherParticipant?.firstName)) && (
                      <>
                        {selectedChat ? (
                          <span class="mailbox-open-date">
                            {new Date(
                              getTimestamp(selectedChat)
                            ).toLocaleDateString()}{" "}
                            {new Date(
                              getTimestamp(selectedChat)
                            ).toLocaleTimeString()}
                          </span>
                        ) : (
                          <span class="mailbox-open-date">New Conversation</span>
                        )}
                        <h5 class="mailbox-open-title">
                          {currentMessageListingDetails &&
                            currentMessageListingDetails.listingTitle != null
                            ? currentMessageListingDetails.listingTitle
                            : selectedChat ? "" : `Message to ${otherParticipant?.firstName} ${otherParticipant?.lastName}`}
                        </h5>
                        <div class="mailbox-open-author">
                          <div class="mailbox-open-author-info">
                            {(() => {
                              const renterProfileId = getRenterProfileUserId({
                                currentUser,
                                currentUserDetails,
                                receiverId,
                                otherParticipant,
                                selectedChat,
                                otherParticipants,
                              });
                              const displayName =
                                currentUserDetails?.role === "Admin" &&
                                selectedChat &&
                                !receiverId
                                  ? (() => {
                                      const sender =
                                        otherParticipants[selectedChat.senderId];
                                      const receiver =
                                        otherParticipants[selectedChat.receiverId];
                                      if (sender && receiver) {
                                        return `${sender.firstName || ""} ${sender.lastName || ""} & ${receiver.firstName || ""} ${receiver.lastName || ""}`.trim();
                                      }
                                      return "Conversation";
                                    })()
                                  : `${otherParticipant?.firstName || ""} ${otherParticipant?.lastName || ""}`.trim() ||
                                    "User";

                              return (
                                <>
                                  {renterProfileId ? (
                                    <Link
                                      to={`/dashboard/renter/${renterProfileId}`}
                                      className="mailbox-open-author-info-name d-block renter-profile-link"
                                    >
                                      {displayName}
                                    </Link>
                                  ) : (
                                    <span class="mailbox-open-author-info-name d-block">
                                      {displayName}
                                    </span>
                                  )}
                                  {renterProfileId && (
                                    <Link
                                      to={`/dashboard/renter/${renterProfileId}`}
                                      className="mailbox-open-view-profile-link"
                                    >
                                      View renter profile
                                    </Link>
                                  )}
                                </>
                              );
                            })()}
                            <span class="mailbox-open-author-info-to">
                              {currentMessageListingDetails &&
                                currentMessageListingDetails.listingTitle != null
                                ? currentMessageListingDetails.listingTitle
                                : ""}
                            </span>
                          </div>
                        </div>
                        <div
                          className="mailbox-open-content-email"
                          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
                        >
                          <div
                            ref={chatBodyRef}
                            onScroll={handleScroll}
                            style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
                          >
                            <div className="row chat">
                              <div className="col">
                                {visibleLimit < (selectedChat?.messages?.length || 0) && (
                                  <div className="text-center mb-2">
                                    <small className="text-muted">Scroll up to load older messages...</small>
                                  </div>
                                )}
                                <div className="row">
                                  <div className="col d-flex flex-column">
                                    {visibleMessages?.map(
                                      (message, index) => (
                                        <div
                                          key={index}
                                          className={`d-flex flex-column chatBubble ${message?.senderId === currentUser?.uid
                                            ? "sent"
                                            : "received"
                                            } ${message.offerStatus === "Pending" ||
                                              message.offerStatus === "pending" ||
                                              message.offerStatus === "sent" ||
                                              message.offerStatus === "Sent" ||
                                              message.offer === 1
                                              ? "flex-column"
                                              : ""
                                            }`}
                                          style={{
                                            alignItems:
                                              message.receiverId ===
                                                currentUser.uid
                                                ? "flex-start"
                                                : "flex-end",
                                          }}
                                        >
                                          <p
                                            style={{
                                              backgroundColor:
                                                message?.status === "canceled"
                                                  ? "rgb(232, 67, 195)"
                                                  : "",
                                            }}
                                            dangerouslySetInnerHTML={{
                                              __html: formatChatMessageHtml(
                                                message.message
                                              ),
                                            }}
                                          />
                                          {message.type === "proposedEdit" ? (
                                            <div className="proposed-edit-card p-3 mt-2 rounded border shadow-sm" style={{ backgroundColor: "#ffffff", color: "#333", maxWidth: "350px" }}>
                                                <h6 className="mb-3 fw-bold" style={{ borderBottom: "1px solid #dee2e6", paddingBottom: "8px", color: "#2c3e50" }}>Proposed Booking Edit</h6>
                                                {message.proposedEditData?.changes && message.proposedEditData.changes.length > 0 ? (
                                                    <div className="mb-2">
                                                        <ul className="mb-0 text-muted" style={{ paddingLeft: "20px", fontSize: "0.95em" }}>
                                                            {message.proposedEditData.changes.map((c, i) => {
                                                                 const fieldLabel = c.field === "Duration" ? "Duration Change" : c.field;
                                                                 const cleanOld = typeof c.old === 'string' ? c.old.replace(/Night's/gi, "Days").replace(/Hour's/gi, "Hours").replace(/'s/g, "s") : c.old;
                                                                 const cleanNew = typeof c.new === 'string' ? c.new.replace(/Night's/gi, "Days").replace(/Hour's/gi, "Hours").replace(/'s/g, "s") : c.new;
                                                                 return (
                                                                     <li key={i} className="mb-1">
                                                                         <strong>{fieldLabel}:</strong> {cleanOld} ➔ {cleanNew}
                                                                     </li>
                                                                 );
                                                             })}
                                                        </ul>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <p className="mb-1" style={{ fontSize: "0.95em" }}><strong>New Date:</strong> {message.proposedEditData?.date}</p>
                                                        <p className="mb-1" style={{ fontSize: "0.95em" }}><strong>New Time:</strong> {message.proposedEditData?.time}</p>
                                                        <p className="mb-2" style={{ fontSize: "0.95em" }}><strong>New Price:</strong> ${message.proposedEditData?.newPrice}</p>
                                                    </>
                                                )}
                                                
                                                {message.proposedEditData?.deltaCents !== 0 && (
                                                    <div className="mt-2 p-2 rounded" style={{ backgroundColor: message.proposedEditData?.deltaCents > 0 ? "#fff3f3" : "#f0fdf4", border: `1px solid ${message.proposedEditData?.deltaCents > 0 ? "#ffcaca" : "#bbf7d0"}` }}>
                                                        <p className={`mb-0 fw-bold ${message.proposedEditData?.deltaCents > 0 ? "text-danger" : "text-success"}`} style={{ fontSize: "0.95em" }}>
                                                            {message.proposedEditData?.deltaCents > 0 ? "Booking Price Increased:" : "Booking Price Decreased:"} ${Math.abs(message.proposedEditData?.deltaCents / 100).toFixed(2)}
                                                        </p>
                                                    </div>
                                                )}
                                                
                                                {message.status === "pending" && currentUser?.uid === message.receiverId && (
                                                    <div className="mt-3">
                                                        <p className="mb-2 text-muted" style={{ fontSize: "0.85em", lineHeight: "1.4" }}>
                                                            {message.proposedEdit?.requiresStripeCharge 
                                                                ? (message.proposedEditData?.deltaCents > 0
                                                                    ? "By accepting, your card on file will be charged the required upfront amount for this change."
                                                                    : "By accepting, the applicable refund will be credited to your original payment method.")
                                                                : (message.proposedEditData?.deltaCents !== 0
                                                                    ? "By accepting, the updated price will apply when you complete your payment."
                                                                    : "By accepting, your booking details will be updated.")}
                                                        </p>
                                                        <div className="d-flex gap-2">
                                                            <Button 
                                                                variant="success" 
                                                                size="sm" 
                                                                className="w-100 fw-bold" 
                                                                disabled={acceptingEditKey === (message.timestamp || message.bookingId)}
                                                                onClick={() => handleAcceptProposedEdit(message)}
                                                            >
                                                                {acceptingEditKey === (message.timestamp || message.bookingId) ? "Accepting..." : "Accept"}
                                                            </Button>
                                                            <Button variant="danger" size="sm" className="w-100 fw-bold" onClick={() => handleDeclineProposedEdit(message)}>
                                                                Decline
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                {message.status === "pending" && currentUser?.uid === message.senderId && (
                                                    <div className="d-flex gap-2 mt-3">
                                                        <Button variant="secondary" size="sm" onClick={() => handleWithdrawProposedEdit(message)}>
                                                            Withdraw Proposal
                                                        </Button>
                                                    </div>
                                                )}
                                                {message.status === "accepted" && (
                                                    <div className="mt-2">
                                                        <div className="text-success fw-bold">
                                                            <i className="material-icons" style={{fontSize: "16px", verticalAlign: "middle"}}>check_circle</i> Accepted
                                                        </div>
                                                        {currentUser?.uid === message.receiverId && !message.proposedEdit?.requiresStripeCharge && !isBookingPaid && (
                                                            <Button 
                                                                variant="primary" 
                                                                size="sm" 
                                                                className="mt-2 w-100 fw-bold shadow-sm" 
                                                                onClick={() => handlePayNowFromProposedEdit(message)}
                                                            >
                                                                Pay Now
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                                {message.status === "declined" && <div className="mt-2 text-danger fw-bold"><i className="material-icons" style={{fontSize: "16px", verticalAlign: "middle"}}>cancel</i> Declined</div>}
                                                {message.status === "withdrawn" && <div className="mt-2 text-secondary fw-bold"><i className="material-icons" style={{fontSize: "16px", verticalAlign: "middle"}}>remove_circle</i> Withdrawn</div>}
                                            </div>
                                          ) : null}
                                          {message.inquiryId != null &&
                                            message.receiverId ===
                                            currentUser.uid &&
                                            otherUserDetails.role ===
                                            "Boat Owner" ? (
                                            message.offer === 1 ? (
                                              <>
                                                <Button
                                                  disabled={
                                                    message?.isUpdateOffer ||
                                                    message?.offerStatus ===
                                                    "Canceled"
                                                  }
                                                  variant="primary offerButton"
                                                  className="m-1"
                                                  onClick={() =>
                                                    openViewOfferModal(message)
                                                  }
                                                >
                                                  {loading ? (
                                                    <ThreeDots
                                                      visible={true}
                                                      height="22"
                                                      width="22"
                                                      color="#fff"
                                                      radius="20"
                                                      ariaLabel="three-dots-loading"
                                                      wrapperStyle={{}}
                                                      wrapperClass=""
                                                    />
                                                  ) : (
                                                    "View Offer"
                                                  )}
                                                </Button>
                                              </>
                                            ) : message.offerStatus ===
                                              "Pending" ||
                                              message.offerStatus ===
                                              "pending" ? (
                                              <>
                                                {loading ? (
                                                  <Button
                                                    variant="primary"
                                                    className="m-1 offerButton"
                                                  >
                                                    {loading && (
                                                      <ThreeDots
                                                        visible={true}
                                                        height="22"
                                                        width="22"
                                                        color="#fff"
                                                        radius="20"
                                                        ariaLabel="three-dots-loading"
                                                        wrapperStyle={{}}
                                                        wrapperClass=""
                                                      />
                                                    )}
                                                  </Button>
                                                ) : (
                                                  <Button
                                                    variant="primary"
                                                    className="m-1 offerButton"
                                                    onClick={() =>
                                                      openCreateOfferModal(
                                                        message.inquiryId,
                                                        message.listingId
                                                      )
                                                    }
                                                  >
                                                    Create Offer
                                                  </Button>
                                                )}
                                              </>
                                            ) : message.offerStatus ===
                                              "Accepted" &&
                                              message.offerAccepted !== null ? (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "12px",
                                                }}
                                              >
                                                <Button
                                                  variant="primary offerButton"
                                                  className="mb-1"
                                                  disabled={
                                                    message?.isUpdateOffer ||
                                                    message?.offerStatus ===
                                                    "Canceled" ||
                                                    message?.offerStatus ===
                                                    "canceled"
                                                  }
                                                  onClick={() =>
                                                    openEditOfferModal(message)
                                                  }
                                                >
                                                  {loading ? (
                                                    <ThreeDots
                                                      visible={true}
                                                      height="22"
                                                      width="22"
                                                      color="#fff"
                                                      radius="20"
                                                      ariaLabel="three-dots-loading"
                                                      wrapperStyle={{}}
                                                      wrapperClass=""
                                                    />
                                                  ) : (
                                                    "Edit Offer"
                                                  )}
                                                </Button>
                                                <div
                                                  className="d-flex"
                                                  style={{
                                                    gap: "5px",
                                                    marginTop: "-8px",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  <i
                                                    style={{ color: "green" }}
                                                    className="material-icons"
                                                  >
                                                    check_circle_outline
                                                  </i>{" "}
                                                  Offer Accepted
                                                </div>
                                              </div>
                                            ) : message.offerStatus === "Sent" &&
                                              message.offerAccepted !== null ? (
                                              <Button
                                                variant="primary offerButton"
                                                className="mb-1"
                                                disabled={
                                                  message?.offerStatus ===
                                                  "Canceled" ||
                                                  message?.offerStatus ===
                                                  "canceled"
                                                }
                                                onClick={() =>
                                                  openEditOfferModal(message)
                                                }
                                              >
                                                {loading ? (
                                                  <ThreeDots
                                                    visible={true}
                                                    height="22"
                                                    width="22"
                                                    color="#fff"
                                                    radius="20"
                                                    ariaLabel="three-dots-loading"
                                                    wrapperStyle={{}}
                                                    wrapperClass=""
                                                  />
                                                ) : (
                                                  "Edit Offer"
                                                )}
                                              </Button>
                                            ) : message?.offerStatus ===
                                              "canceled" ? (
                                              <Button
                                                variant="primary"
                                                style={{
                                                  backgroundColor:
                                                    "rgb(232, 67, 195)",
                                                  color: "white",
                                                  border: "none",
                                                }}
                                                className="mb-4"
                                              >
                                                Offer canceled
                                              </Button>
                                            ) : (
                                              ""
                                            )
                                          ) : (message.inquiryId != null &&
                                            otherUserDetails.role ===
                                            "Renter") ||
                                            (otherUserDetails.role ===
                                              "Boat Owner" &&
                                              message.receiverId ===
                                              currentUser.uid) ? (
                                            message.offer === undefined &&
                                              message.offerStatus === "Accepted" ? (
                                              // Your additional condition is met - Display whatever you want here
                                              ""
                                            ) : message.offer === 1 &&
                                              message.offerStatus !==
                                              "Accepted" &&
                                              message.offerStatus !==
                                              "Canceled" &&
                                              message.status !== "canceled" ? (
                                              <>
                                                <Button
                                                  disabled={
                                                    message?.disableViewOfferBTN ||
                                                    message?.offerStatus ===
                                                    "Canceled" ||
                                                    false
                                                  }
                                                  variant="primary offerButton"
                                                  className="m-1"
                                                  onClick={() =>
                                                    openViewOfferModal(message)
                                                  }
                                                >
                                                  {loading ? (
                                                    <ThreeDots
                                                      visible={true}
                                                      height="22"
                                                      width="22"
                                                      color="#fff"
                                                      radius="20"
                                                      ariaLabel="three-dots-loading"
                                                      wrapperStyle={{}}
                                                      wrapperClass=""
                                                    />
                                                  ) : (
                                                    "View Offer"
                                                  )}
                                                </Button>
                                              </>
                                            ) : message.offerStatus ===
                                              "Accepted" &&
                                              message.offerAccepted !== null ? (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "12px",
                                                }}
                                              >
                                                {otherUserDetails.role !==
                                                  "Renter" && (
                                                  <Button
                                                    variant="primary offerButton"
                                                    className="mb-1"
                                                    disabled={
                                                      message?.offerStatus ===
                                                      "Canceled" ||
                                                      message?.offerStatus ===
                                                      "canceled" ||
                                                      message?.status ===
                                                      "canceled"
                                                    }
                                                    onClick={() =>
                                                      openEditOfferModal(message)
                                                    }
                                                  >
                                                    {loading ? (
                                                      <ThreeDots
                                                        visible={true}
                                                        height="22"
                                                        width="22"
                                                        color="#fff"
                                                        radius="20"
                                                        ariaLabel="three-dots-loading"
                                                        wrapperStyle={{}}
                                                        wrapperClass=""
                                                      />
                                                    ) : (
                                                      "Edit Offer"
                                                    )}
                                                  </Button>
                                                )}
                                                <div
                                                  className="d-flex"
                                                  style={{
                                                    gap: "5px",
                                                    marginTop: "-8px",
                                                    fontWeight: "500",
                                                  }}
                                                >
                                                  <i
                                                    style={{ color: "green" }}
                                                    className="material-icons"
                                                  >
                                                    check_circle_outline
                                                  </i>{" "}
                                                  Offer Accepted
                                                </div>
                                              </div>
                                            ) : message.offerStatus === " " ? (
                                              <i className="material-icons">
                                                check_circle_outline
                                              </i>
                                            ) : null
                                          ) : null}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row sender pt-2">
                            <div className="col-12 col-md-10">
                              <div class="form-floating">
                                <input
                                  type="text"
                                  placeholder="Send Message..."
                                  className="form-control form-control-solid-bordered"
                                  aria-describedby="..."
                                  id="floatingInput"
                                  value={message}
                                  onChange={(e) => setMessage(e.target.value)}
                                  onKeyDown={handleKeyPress}
                                />
                                <label for="floatingInput">Send Message</label>
                              </div>
                            </div>
                            <div className="col-12 col-md-2 d-flex">
                              <button
                                type="button"
                                class="btn send-btn btn-primary"
                                onClick={sendMessage}
                              >
                                <i class="material-icons-outlined">send</i>Send
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        centered
        show={isCreateOfferModalOpen}
        onHide={closeCreateOfferModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {otherUserDetails.role === "Boat Owner" &&
              selectedMessageData.receiverId != currentUser.uid
              ? "Create Offer"
              : "Offer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {otherUserDetails.role === "Boat Owner" &&
              selectedMessageData?.receiverId != currentUser.uid ? (
              <Row>
                <Form.Label className="inquiry-label">
                  Estimated Platform Price: {calculateEstimatedPricing() || 0}
                </Form.Label>

                {error && (
                  <Alert className="mt-3" severity="error">
                    {error}
                  </Alert>
                )}
                <Col>
                  <Form.Group controlId="priceInput">
                    <Form.Label>Price</Form.Label>
                    {calculateEstimatedPricing() !== "" && (
                      <CurrencyInput
                        id="offerPrice"
                        name="offerPrice"
                        placeholder="Enter Price"
                        defaultValue={calculateEstimatedPricing() || 0}
                        decimalsLimit={2}
                        prefix="$"
                        value={calculateEstimatedPricing() || 0}
                        className="form-control"
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>
            ) : (
              ""
            )}
            <Row>
              <Col>
                {otherUserDetails.role === "Boat Owner" &&
                  selectedMessageData.receiverId != currentUser.uid ? (
                  <h5 className="mt-4">
                    Renter Requests for{" "}
                    {currentMessageListingDetails?.listingTitle}{" "}
                  </h5>
                ) : (
                  <h5>Your Specifications</h5>
                )}
                <Row>
                  <Col>
                    {inquiryDetails ? (
                      <>
                        {inquiryDetails?.inquiryData?.estimatedPrice != null && (
                          <p className="mb-1">
                            • $
                            {parseInt(
                              inquiryDetails?.inquiryData?.estimatedPrice
                            ) || 0}
                          </p>
                        )}
                        {currentMessageListingDetails &&
                          currentMessageListingDetails.listingTitle != null
                          ? "• " + currentMessageListingDetails.listingTitle
                          : ""}
                        {inquiryDetails.inquiryData && (
                          <>
                            <p className="mb-1">
                              • {inquiryDetails.inquiryData?.prefferedDate}
                            </p>

                            <p className="mb-1">
                              • {inquiryDetails.inquiryData?.prefferedTime}
                            </p>
                            {boatDetailsById?.boatType?.toLowerCase() ===
                              "kayak" ||
                              boatDetailsById?.boatType?.toLowerCase() ===
                              "jetski" ? (
                              <p className="mb-1">
                                •{" "}
                                {inquiryDetails?.inquiryData?.numberOfBoats > 1
                                  ? `${inquiryDetails?.inquiryData?.numberOfBoats} Boats needed`
                                  : `${inquiryDetails?.inquiryData?.numberOfBoats} Boat needed`}
                              </p>
                            ) : (
                              ""
                            )}

                            {inquiryDetails.inquiryData?.overnights > 0 && (
                              <p className="mb-1">
                                • {inquiryDetails.inquiryData?.overnights}{" "}
                                Nights
                              </p>
                            )}
                            {inquiryDetails.inquiryData?.sameDayHours > 0 && (
                              <p className="mb-1">
                                • {inquiryDetails.inquiryData?.sameDayHours}{" "}
                                Hours
                              </p>
                            )}
                            {inquiryDetails.inquiryData?.sameDayMinutes > 0 && (
                              <p className="mb-1">
                                • {inquiryDetails.inquiryData?.sameDayMinutes}{" "}
                                Minutes
                              </p>
                            )}

                            <InquiryGuestLine inquiryData={inquiryDetails.inquiryData} />
                            {inquiryDetails.inquiryData?.comments != null && (
                              <>
                                <h6 className="mt-4">Comments:</h6>
                                <p className="mb-1">
                                  • {inquiryDetails.inquiryData?.comments}
                                </p>
                              </>
                            )}

                            {(otherUserDetails?.role !== "Boat Owner" ||
                              selectedMessageData?.receiverId ===
                              currentUser?.uid) && (
                                <>
                                  <h5 className="mt-4">Offer Details</h5>
                                  <p className="mb-1">
                                    • Quote, $
                                    {inquiryDetails.offerData?.offerPrice}
                                  </p>
                                  <p className="mb-1">
                                    • Time offered,{" "}
                                    {inquiryDetails.offerData?.offeredDate} at{" "}
                                    {moment(
                                      inquiryDetails?.offerData?.offeredTime,
                                      "HH:mm"
                                    ).format("h:mm A")}
                                  </p>
                                  <p className="mb-1">
                                    • Duration,{" "}
                                    {inquiryDetails?.offerData?.duration}
                                  </p>
                                  {offerAddresses?.address?.pickupAddress ? (
                                    <p className="mb-1">
                                      • Pickup Location,{" "}
                                      {offerAddresses?.address?.pickupAddress}{" "}
                                    </p>
                                  ) : (
                                    ""
                                  )}
                                  {offerAddresses?.address?.dropOffAddress ? (
                                    <p className="mb-1">
                                      • Dropoff Location,{" "}
                                      {offerAddresses?.address?.dropOffAddress}
                                    </p>
                                  ) : (
                                    ""
                                  )}
                                </>
                              )}
                          </>
                        )}
                      </>
                    ) : null}
                  </Col>
                </Row>
              </Col>
            </Row>

            {otherUserDetails.role === "Boat Owner" &&
              selectedMessageData.receiverId != currentUser.uid ? (
              <div className="mt-4">
                {/* <h5>Offer Details</h5> */}
                <div class="settings-integrations-item mb-4">
                  <Row>
                    <Col xs={12}>
                      <Button
                        style={{ width: "100%", padding: "15px 0px" }}
                        variant="primary"
                        onClick={() => handleToggleChange}
                      >
                        Modify Offer
                      </Button>
                    </Col>
                  </Row>
                </div>

                {offerExpiryToggle && (
                  <div>
                    <Row>
                      <Col xs={6}>
                        <Form.Group controlId="offeredDateInput">
                          <Form.Label>Offer Date (Pickup Date):</Form.Label>

                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              sx={{
                                height: "auto",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                "& .MuiInputBase-root": {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& .MuiInputBase-root.Mui-focused": {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& .MuiInputBase-root.Mui-error": {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& .MuiInputBase-root.Mui-disabled": {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& .MuiInputBase-root.Mui-error.Mui-disabled": {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& .MuiInputBase-root.Mui-error.Mui-disabled.Mui-focused":
                                {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& .MuiInputBase-root.Mui-error.Mui-disabled.Mui-focused.Mui-error.Mui-disabled.Mui-focused":
                                {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& .MuiInputBase-root.Mui-error.Mui-disabled.Mui-focused.Mui-error.Mui-disabled.Mui-focused.Mui-error.Mui-disabled.Mui-focused.Mui-error":
                                {
                                  height: "auto",
                                  padding: "10px",
                                },
                                "& input": {
                                  height: "auto",
                                  padding: "0px",
                                },
                              }}
                              minDate={dayjs().startOf("day")}
                              value={dayjs(offeredDate)}
                              onChange={(date) =>
                                setOfferedDate(dayjs(date).format("YYYY-MM-DD"))
                              }
                              label=""
                              required
                              shouldDisableDate={(date) => {
                                const formattedDate =
                                  dayjs(date).format("YYYY-MM-DD");
                                const normalizedDisabledDates =
                                  disabledDates.map((d) =>
                                    dayjs(d).format("YYYY-MM-DD")
                                  );
                                return normalizedDisabledDates.includes(
                                  formattedDate
                                );
                              }}
                              disablePast
                            />
                          </LocalizationProvider>
                        </Form.Group>
                      </Col>
                      <Col xs={6}>
                        <Form.Group controlId="offeredTimeInput">
                          <Form.Label>Offer Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={
                              offeredTime ||
                              convertTime(
                                inquiryDetails.inquiryData?.prefferedTime
                              )
                            }
                            onChange={(e) => handleOfferedTime(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs={6}>
                        <Form.Label className="mt-3 inquiry-label">
                          Pickup Location
                        </Form.Label>
                        <div className="locationDiv">
                          <PlacesAutocomplete
                            value={offerAddresses.address.pickupAddress}
                            onChange={(e) =>
                              setOfferAddresses((prev) => {
                                return {
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    pickupAddress: e,
                                  },
                                };
                              })
                            }
                            onSelect={handlePSelect}
                          >
                            {({
                              getInputProps,
                              suggestions,
                              getSuggestionItemProps,
                              loading,
                            }) => (
                              <div>
                                <div className="input-group">
                                  <input
                                    title={offerAddresses.address.pickupAddress}
                                    {...getInputProps({
                                      placeholder: "Search Places...",
                                      className:
                                        "location-search-input form-control",
                                      name: "location",
                                    })}
                                  />
                                  {/* <div className="input-group-append d-flex">
                                    <button
                                      className="btn btn-outline-dark"
                                      type="button"
                                      onClick={handleCurrentLocation}
                                    >
                                      <i className="fas fa-map-marker-alt"></i>
                                    </button>
                                  </div> */}
                                </div>
                                <div className="autocomplete-dropdown-container">
                                  {loading && <div>Loading...</div>}
                                  {suggestions.map((suggestion) => {
                                    const className = suggestion.active
                                      ? "suggestion-item--active"
                                      : "suggestion-item";
                                    return (
                                      <div
                                        {...getSuggestionItemProps(suggestion, {
                                          className:
                                            "location-suggestion form-control",
                                        })}
                                      >
                                        <span>{suggestion.description}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </PlacesAutocomplete>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <Form.Label className="mt-3 inquiry-label">
                          Drop Off Location
                        </Form.Label>
                        <div className="locationDiv">
                          <PlacesAutocomplete
                            value={offerAddresses.address.dropOffAddress}
                            onChange={(e) =>
                              setOfferAddresses((prev) => {
                                return {
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    dropOffAddress: e,
                                  },
                                };
                              })
                            }
                            onSelect={handleDropOffSelect}
                          >
                            {({
                              getInputProps,
                              suggestions,
                              getSuggestionItemProps,
                              loading,
                            }) => (
                              <div>
                                <div className="input-group">
                                  <input
                                    title={
                                      offerAddresses.address.dropOffAddress
                                    }
                                    {...getInputProps({
                                      placeholder: "Search Places...",
                                      className:
                                        "location-search-input form-control",
                                      name: "location",
                                    })}
                                  />
                                  {/* <div className="input-group-append d-flex">
                                    <button
                                      className="btn btn-outline-dark"
                                      type="button"
                                      onClick={handleCurrentLocation}
                                    >
                                      <i className="fas fa-map-marker-alt"></i>
                                    </button>
                                  </div> */}
                                </div>
                                <div className="autocomplete-dropdown-container">
                                  {loading && <div>Loading...</div>}
                                  {suggestions.map((suggestion) => {
                                    const className = suggestion.active
                                      ? "suggestion-item--active"
                                      : "suggestion-item";
                                    return (
                                      <div
                                        {...getSuggestionItemProps(suggestion, {
                                          className:
                                            "location-suggestion form-control",
                                        })}
                                      >
                                        <span>{suggestion.description}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </PlacesAutocomplete>
                        </div>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      {((currentMessageListingDetails?.pricing?.hourlyRate &&
                        parseFloat(currentMessageListingDetails?.pricing?.hourlyRate) > 0) ||
                        currentMessageListingDetails?.boatType === "Bass Boat") && (
                          <Col xs={6} className="text-center">
                            <Row>
                              <Col>
                                <Form.Label className="inquiry-label">
                                  Hours
                                </Form.Label>
                              </Col>
                            </Row>
                            <Row>
                              <Col className="d-flex" style={{ gap: "5px" }}>
                                <Button
                                  onClick={() => handleRemove("sameDayHours")}
                                  className="d-flex justify-content-center align-items-center"
                                  style={{ width: "10px" }}
                                >
                                  <i className="material-icons add-remove">
                                    remove_circle
                                  </i>
                                </Button>
                                <Form.Control
                                  type="number"
                                  name="sameDayHours"
                                  className="form-control"
                                  value={sameDayHours}
                                  onChange={(e) =>
                                    handleSameDayHours(e.target.value)
                                  }
                                  required
                                />
                                <Button
                                  onClick={() => handleAdd("sameDayHours")}
                                  className="d-flex justify-content-center align-items-center"
                                  style={{ width: "10px" }}
                                >
                                  <i className="material-icons add-remove">
                                    add_circle
                                  </i>
                                </Button>
                              </Col>
                            </Row>
                          </Col>
                        )}

                      <Col xs={6} className="text-center">
                        <Row>
                          {currentMessageListingDetails &&
                            (currentMessageListingDetails?.pricing?.dailyOptions?.length > 0) && (
                              <>
                                <Row>
                                  <Col>
                                    <Form.Label className="inquiry-label">
                                      Overnight
                                    </Form.Label>
                                    <div className="d-flex justify-content-center align-items-center gap-3">
                                      <Form.Select
                                        name="overnight"
                                        value={overnight}
                                        onChange={(e) =>
                                          handleOvernight(e.target.value)
                                        }
                                        className="form-select"
                                        style={{ width: "90%" }}
                                        required
                                      >
                                        <option value="" default>
                                          Select An Option
                                        </option>
                                        {currentMessageListingDetails.pricing &&
                                          currentMessageListingDetails.pricing
                                            .dailyOptions &&
                                          currentMessageListingDetails.pricing
                                            .dailyOptions.length > 0
                                          ? currentMessageListingDetails.pricing.dailyOptions.map(
                                            (option) => (
                                              <option
                                                key={option.days}
                                                value={option.days}
                                              >
                                                {`${option.days} Days (${option.days * 24
                                                  } hours)`}
                                              </option>
                                            )
                                          )
                                          : null}
                                      </Form.Select>
                                    </div>
                                  </Col>
                                </Row>
                              </>
                            )}
                        </Row>
                      </Col>
                    </Row>
                    {currentMessageListingDetails &&
                      currentMessageListingDetails?.pricing &&
                      currentMessageListingDetails?.pricing?.hourlyOptions &&
                      currentMessageListingDetails?.pricing?.hourlyOptions?.length > 0 && (
                        <>
                          <Row>
                            <Col xs={12} className="text-center mt-3">
                              <Form.Label className="inquiry-label">
                                Hourly Packages
                              </Form.Label>
                              <div className="d-flex justify-content-center align-items-center gap-3">
                                <Form.Select
                                  name="sameDayHours"
                                  id="sameDayHours"
                                  value={sameDayHours}
                                  onChange={(e) =>
                                    handleSameDayHours(e.target.value)
                                  }
                                  className="form-select"
                                  required
                                >
                                  <option value="" default>
                                    Select An Option
                                  </option>
                                  {currentMessageListingDetails.pricing.hourlyOptions.map(
                                    (option) => (
                                      <option
                                        key={option.hours}
                                        value={option.hours}
                                      >
                                        {`${option.hours} Hours`}
                                      </option>
                                    )
                                  )}
                                </Form.Select>
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                    <Row className="mt-3">
                      <FormGroup className="d-flex">
                        {!isFishingCharterListing(currentMessageListingDetails) && (
                          <>
                        <h5 className="mt-3">Additionals:</h5>
                        {boatDetailsById?.deliveryAndPickup && (
                          <div className="d-flex">
                            <FormControlLabel
                              style={{ alignItems: "flex-start" }}
                              control={
                                <Checkbox
                                  checked={deliveryPickupCheckbox}
                                  onChange={handleDeliveryPickupCheckboxChange}
                                />
                              }
                            />
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <p
                                style={{
                                  fontWeight: "500",
                                  lineHeight: "28px",
                                }}
                              >
                                Price Quote for Pickup & Delivery.
                              </p>
                            </div>
                          </div>
                        )}
                        {currentMessageListingDetails &&
                          currentMessageListingDetails.boatType !== "Fishing Guide" &&
                          (currentMessageListingDetails.boatType ===
                            "Bass Boat" ||
                            (currentMessageListingDetails.boatType !==
                              "Bass Boat" &&
                              currentMessageListingDetails.pricing &&
                              currentMessageListingDetails.pricing
                                .overnightEnabled)) && (
                            <>
                              <div className="d-flex mt-1">
                                <FormControlLabel
                                  style={{ alignItems: "flex-start" }}
                                  control={
                                    <Checkbox
                                      // disabled={
                                      //   (overnight && overnight > 0) ||
                                      //   sameDayHours >= 24
                                      // } // Modified the disabled condition
                                      checked={trailerCheckbox}
                                      onChange={handleTrailerCheckbox}
                                    />
                                  }
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontWeight: "500",
                                      lineHeight: "28px",
                                    }}
                                  >
                                    Price Quote for Trailer Package.
                                    {overnight > 0 && (
                                      <span style={{ color: "red" }}> *</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </>
                          )}

                        {currentMessageListingDetails &&
                          currentMessageListingDetails.boatType !== "Fishing Guide" && (
                            <div className="d-flex mt-1">
                              <FormControlLabel
                                style={{ alignItems: "flex-start" }}
                                control={
                                  <Checkbox
                                    checked={captainCheckbox}
                                    onChange={handleCaptainCheckbox}
                                  />
                                }
                              />
                              <div
                                style={{ display: "flex", alignItems: "center" }}
                              >
                                <p
                                  style={{ fontWeight: "500", lineHeight: "28px" }}
                                >
                                  Price Quote to Provide Captain.
                                </p>
                              </div>
                            </div>
                          )}
                          </>
                        )}
                        <Row>
                          {currentMessageListingDetails &&
                          !isFishingCharterListing(currentMessageListingDetails) &&
                          currentMessageListingDetails.boatType !== "Fishing Guide" && (
                            <Col xs={6}>
                              <Form.Group controlId="priceInput">
                                <Form.Label>Additional Cost</Form.Label>
                                <CurrencyInput
                                  id="additionalCost"
                                  name="additionalCost"
                                  placeholder="Enter Price"
                                  defaultValue={0}
                                  decimalsLimit={2}
                                  prefix="$"
                                  value={additionalCost}
                                  onValueChange={(value) =>
                                    handleAdditionalCost(value)
                                  }
                                  className="form-control"
                                />
                              </Form.Group>
                            </Col>
                          )}
                          <Col xs={6}>
                            <Form.Group controlId="priceInput">
                              <Form.Label>Discount</Form.Label>
                              <CurrencyInput
                                id="ownerDiscount"
                                name="ownerDiscount"
                                placeholder="Enter discount"
                                defaultValue={0}
                                decimalsLimit={2}
                                prefix="$"
                                value={ownerDiscount}
                                onValueChange={(value) =>
                                  handleOwnerDiscount(value)
                                }
                                className="form-control"
                              />
                            </Form.Group>
                            {error && (
                              <Alert className="mt-3" severity="error">
                                {error}
                              </Alert>
                            )}
                          </Col>
                        </Row>
                      </FormGroup>
                      <Col xs={12} className="text-center mt-3">
                        <Row>
                          <Col>
                            <Form.Label className="inquiry-label">
                              Duration: {offeredDuration}
                            </Form.Label>
                            <Form.Control
                              type="hidden"
                              name="offeredduration"
                              className="form-control"
                              value={offeredDuration}
                              required
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={12} className="text-center mt-3">
                        <Row>
                          <Col>
                            <Form.Label className="inquiry-label">
                              Ariving Date & Time: {arivingDateTime}
                            </Form.Label>
                            <Form.Control
                              type="hidden"
                              name="arivingdatetime"
                              value={arivingDateTime}
                              className="form-control"
                              required
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            ) : (
              ""
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCreateOfferModal}>
            {otherUserDetails.role === "Boat Owner" ? "Close" : "Reject Offer"}
          </Button>
          {otherUserDetails.role === "Boat Owner" &&
            selectedMessageData.receiverId != currentUser.uid ? (
            <>
              {loading ? (
                <Button variant="primary">
                  {loading && (
                    <ThreeDots
                      visible={true}
                      height="22"
                      width="22"
                      color="#fff"
                      radius="20"
                      ariaLabel="three-dots-loading"
                      wrapperStyle={{}}
                      wrapperClass=""
                    />
                  )}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  disabled={error}
                  onClick={handleSendOffer}
                >
                  Create Offer
                </Button>
              )}
            </>
          ) : (
            ""
          )}

          {otherUserDetails.role === "Renter" ||
            selectedMessageData.receiverId === currentUser.uid ? (
            <Button
              variant="primary"
              disabled={buttonDisabled}
              onClick={() => {
                if (loading) return;
                handleAcceptOffer();
              }}
            >
              {loading ? (
                <ThreeDots
                  visible={true}
                  height="22"
                  width="22"
                  color="#fff"
                  radius="20"
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                />
              ) : (
                "Accept Offer"
              )}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>

      <Modal centered show={isEditOfferModalOpen} onHide={closeEditOfferModal}>
        <Modal.Header closeButton>Edit Offer</Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Form.Label className="inquiry-label">
                Estimated Platform Price: {calculateEstimatedPricing() || 0}
              </Form.Label>
              {/* <Form.Label className="inquiry-label mt-3">
              Price adjustments: {calculateEstimatedPricing() !== '' ? 
              `${deliveryPickupCheckbox ? '$'+ 0.95 *parseFloat(calculateEstimatedPricing() + 400) 
              : '$'+ 0.95 *calculateEstimatedPricing()}` : ''} 
              - {calculateEstimatedPricing() !== '' ? `${deliveryPickupCheckbox ? '$'+ 1.05 *parseFloat(calculateEstimatedPricing() + 400) : '$'+ 1.05 *calculateEstimatedPricing()}` : ''}
              </Form.Label> */}
              {error && (
                <Alert className="mt-3" severity="error">
                  {error}
                </Alert>
              )}
              <Col>
                <Form.Group controlId="priceInput">
                  <Form.Label>Price</Form.Label>
                  {calculateEstimatedPricing() !== "" && (
                    <CurrencyInput
                      id="offerPrice"
                      name="offerPrice"
                      placeholder="Enter Price"
                      defaultValue={calculateEstimatedPricing()}
                      decimalsLimit={2}
                      prefix="$"
                      value={calculateEstimatedPricing()}
                      className="form-control"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Row>
                  <Col>
                    {
                      // Display details if inquiryDetails is available
                      inquiryDetails ? (
                        <>
                          {currentMessageListingDetails &&
                            currentMessageListingDetails.listingTitle != null
                            ? "• " + currentMessageListingDetails.listingTitle
                            : ""}
                          {inquiryDetails.inquiryData && (
                            <>
                              <p className="mb-1">
                                • {inquiryDetails.inquiryData?.prefferedDate}
                              </p>

                              <p className="mb-1">
                                • {inquiryDetails.inquiryData?.prefferedTime}
                              </p>
                              {inquiryDetails.inquiryData?.overnights > 0 && (
                                <p className="mb-1">
                                  • {inquiryDetails.inquiryData?.overnights}{" "}
                                  Nights
                                </p>
                              )}
                              {inquiryDetails.inquiryData?.sameDayHours > 0 && (
                                <p className="mb-1">
                                  • {inquiryDetails.inquiryData?.sameDayHours}{" "}
                                  Hours
                                </p>
                              )}
                              {inquiryDetails.inquiryData?.sameDayMinutes >
                                0 && (
                                  <p className="mb-1">
                                    • {inquiryDetails.inquiryData?.sameDayMinutes}{" "}
                                    Minutes
                                  </p>
                                )}

                              <InquiryGuestLine inquiryData={inquiryDetails.inquiryData} />
                              {inquiryDetails.inquiryData?.comments != null && (
                                <>
                                  <h6 className="mt-4">Comments:</h6>
                                  <p className="mb-1">
                                    • {inquiryDetails.inquiryData?.comments}
                                  </p>
                                </>
                              )}

                              {otherUserDetails.role !== "Boat Owner" ||
                                (selectedMessageData.receiverId ===
                                  currentUser.uid && (
                                    <>
                                      <h5 className="mt-4">Offer Details</h5>
                                      <p className="mb-1">
                                        • Quote, $
                                        {inquiryDetails?.offerData?.offerPrice +
                                          (!isNaN(additionalCost) &&
                                            additionalCost > 0
                                            ? parseInt(additionalCost)
                                            : 0) -
                                          (!isNaN(ownerDiscount) &&
                                            ownerDiscount > 0
                                            ? parseInt(ownerDiscount)
                                            : 0)}{" "}
                                      </p>
                                      <p className="mb-1">
                                        • Time offered,{" "}
                                        {inquiryDetails.offerData?.offeredDate} at{" "}
                                        {formatTimeDisplay(
                                          inquiryDetails?.offerData?.offeredTime
                                        )}
                                      </p>
                                      <p className="mb-1">
                                        • Duration,{" "}
                                        {inquiryDetails.offerData?.duration}
                                      </p>
                                      {offerAddresses.address.pickupAddress ? (
                                        <p className="mb-1">
                                          • Pickup Location,{" "}
                                          {offerAddresses.address.pickupAddress}{" "}
                                        </p>
                                      ) : (
                                        ""
                                      )}
                                      {offerAddresses.address.dropOffAddress ? (
                                        <p className="mb-1">
                                          • Dropoff Location,{" "}
                                          {offerAddresses.address.dropOffAddress}
                                        </p>
                                      ) : (
                                        ""
                                      )}
                                    </>
                                  ))}
                            </>
                          )}
                        </>
                      ) : null
                    }
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col>
                <h5 className="mt-4">
                  Renter Requests for{" "}
                  {currentMessageListingDetails?.listingTitle}{" "}
                </h5>
              </Col>
            </Row>
            <div className="mt-4">
              {/* <h5>Offer Details</h5> */}
              <div class="settings-integrations-item mb-4">
                <Row>
                  <Col xs={12} className="d-flex">
                    <Button
                      style={{ width: "100%", padding: "15px 0px" }}
                      variant="primary"
                      onClick={() => handleToggleChange}
                    >
                      Modify Offer
                    </Button>
                  </Col>
                </Row>
              </div>
              {console.log("offeredDateofferedDateofferedDate\n", {
                offeredDate,
                offeredTime,
              })}

              {offerExpiryToggle && (
                <div>
                  <Row>
                    <Col xs={6}>
                      <Form.Group controlId="offeredDateInput">
                        <Form.Label>Offer Date (Pickup Date):</Form.Label>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            sx={{
                              height: "auto",
                              border: "1px solid #ccc",
                              borderRadius: "8px",
                              "& .MuiInputBase-root": {
                                height: "auto",
                                padding: "10px",
                              },
                              "& .MuiInputBase-root.Mui-focused": {
                                height: "auto",
                                padding: "10px",
                              },
                              "& .MuiInputBase-root.Mui-error": {
                                height: "auto",
                                padding: "10px",
                              },
                              "& .MuiInputBase-root.Mui-disabled": {
                                height: "auto",
                                padding: "10px",
                              },
                              "& .MuiInputBase-root.Mui-error.Mui-disabled": {
                                height: "auto",
                                padding: "10px",
                              },
                              "& .MuiInputBase-root.Mui-error.Mui-disabled.Mui-focused":
                              {
                                height: "auto",
                                padding: "10px",
                              },
                              "& .MuiInputBase-root.Mui-error.Mui-disabled.Mui-focused.Mui-error.Mui-disabled.Mui-focused":
                              {
                                height: "auto",
                                padding: "10px",
                              },
                              "& .MuiInputBase-root.Mui-error.Mui-disabled.Mui-focused.Mui-error.Mui-disabled.Mui-focused.Mui-error.Mui-disabled.Mui-focused.Mui-error":
                              {
                                height: "auto",
                                padding: "10px",
                              },
                              "& input": {
                                height: "auto",
                                padding: "0px",
                              },
                            }}
                            minDate={dayjs().startOf("day")}
                            value={dayjs(offeredDate)}
                            onChange={(date) =>
                              setOfferedDate(dayjs(date).format("YYYY-MM-DD"))
                            }
                            label=""
                            required
                            shouldDisableDate={(date) => {
                              const formattedDate =
                                dayjs(date).format("YYYY-MM-DD");
                              const normalizedDisabledDates = disabledDates.map(
                                (d) => dayjs(d).format("YYYY-MM-DD")
                              );
                              return normalizedDisabledDates.includes(
                                formattedDate
                              );
                            }}
                            disablePast
                          />
                        </LocalizationProvider>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group controlId="offeredTimeInput">
                        <Form.Label>Offer Time</Form.Label>
                        <Form.Control
                          className="!h-[66px]"
                          type="time"
                          value={offeredTime}
                          onChange={(e) => handleOfferedTime(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={6}>
                      <Form.Label className="mt-3 inquiry-label">
                        Pickup Location
                      </Form.Label>
                      <div className="locationDiv">
                        <PlacesAutocomplete
                          value={offerAddresses.address.pickupAddress}
                          onChange={(e) =>
                            setOfferAddresses((prev) => {
                              return {
                                ...prev,
                                address: { ...prev.address, pickupAddress: e },
                              };
                            })
                          }
                          onSelect={handlePSelect}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                            loading,
                          }) => (
                            <div>
                              <div className="input-group">
                                <input
                                  title={offerAddresses.address.pickupAddress}
                                  {...getInputProps({
                                    placeholder: "Search Places...",
                                    className:
                                      "location-search-input form-control",
                                    name: "location",
                                  })}
                                />
                              </div>
                              <div className="autocomplete-dropdown-container">
                                {loading && <div>Loading...</div>}
                                {suggestions.map((suggestion) => {
                                  const className = suggestion.active
                                    ? "suggestion-item--active"
                                    : "suggestion-item";
                                  return (
                                    <div
                                      {...getSuggestionItemProps(suggestion, {
                                        className:
                                          "location-suggestion form-control",
                                      })}
                                    >
                                      <span>{suggestion.description}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </PlacesAutocomplete>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="mt-3 inquiry-label">
                        Drop Off Location
                      </Form.Label>
                      <div className="locationDiv">
                        <PlacesAutocomplete
                          value={offerAddresses.address.dropOffAddress}
                          onChange={(e) =>
                            setOfferAddresses((prev) => {
                              return {
                                ...prev,
                                address: { ...prev.address, dropOffAddress: e },
                              };
                            })
                          }
                          onSelect={handleDropOffSelect}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                            loading,
                          }) => (
                            <div>
                              <div className="input-group">
                                <input
                                  title={offerAddresses.address.dropOffAddress}
                                  {...getInputProps({
                                    placeholder: "Search Places...",
                                    className:
                                      "location-search-input form-control",
                                    name: "location",
                                  })}
                                />
                              </div>
                              <div className="autocomplete-dropdown-container">
                                {loading && <div>Loading...</div>}
                                {suggestions.map((suggestion) => {
                                  const className = suggestion.active
                                    ? "suggestion-item--active"
                                    : "suggestion-item";
                                  return (
                                    <div
                                      {...getSuggestionItemProps(suggestion, {
                                        className:
                                          "location-suggestion form-control",
                                      })}
                                    >
                                      <span>{suggestion.description}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </PlacesAutocomplete>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    {((currentMessageListingDetails?.pricing?.hourlyRate &&
                      parseFloat(currentMessageListingDetails?.pricing?.hourlyRate) > 0) ||
                      currentMessageListingDetails?.boatType === "Bass Boat") && (
                        <Col xs={6} className="text-center">
                          <Row>
                            <Col>
                              <Form.Label className="inquiry-label">
                                Hours
                              </Form.Label>
                            </Col>
                          </Row>
                          <Row>
                            <Col className="d-flex" style={{ gap: "5px" }}>
                              <Button
                                onClick={() => handleRemove("sameDayHours")}
                                className="d-flex justify-content-center align-items-center"
                                style={{ width: "10px" }}
                              >
                                <i className="material-icons add-remove">
                                  remove_circle
                                </i>
                              </Button>
                              <Form.Control
                                type="number"
                                name="sameDayHours"
                                className="form-control"
                                value={sameDayHours}
                                onChange={(e) => {
                                  if (
                                    e.target.value === "" ||
                                    e.target.value === null ||
                                    e.target.value === undefined
                                  ) {
                                    handleSameDayHours(0);
                                    return;
                                  }
                                  handleSameDayHours(
                                    Number(e.target.value).toFixed()
                                  );
                                }}
                                required
                              />
                              <Button
                                onClick={() => handleAdd("sameDayHours")}
                                className="d-flex justify-content-center align-items-center"
                                style={{ width: "10px" }}
                              >
                                <i className="material-icons add-remove">
                                  add_circle
                                </i>
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      )}

                    {/* <Col xs={6} className='text-center'>
                      <Row>
                        <Col>
                          <Form.Label className="inquiry-label">Minutes</Form.Label>
                        </Col>
                      </Row>
                      <Row>
                        <Col className='d-flex' style={{gap: '5px'}}>
                          <Button onClick={() => handleRemove('sameDayMinutes')} className='d-flex justify-content-center align-items-center' style={{width: '10px'}}>
                            <i className="material-icons add-remove">remove_circle</i>
                          </Button>
                          <Form.Control
                            type="number"
                            name="sameDayMinutes"
                            className="form-control"
                            value={sameDayMinutes}
                            required
                          />
                          <Button onClick={() => handleAdd('sameDayMinutes')} className='d-flex justify-content-center align-items-center' style={{width: '10px'}}>
                            <i className="material-icons add-remove">add_circle</i>
                          </Button>
                        </Col>
                      </Row>
                    </Col> */}
                    <Col xs={6} className="text-center">
                      <Row>
                        {currentMessageListingDetails &&
                          (currentMessageListingDetails?.pricing?.dailyOptions?.length > 0) && (
                            <>
                              <Row>
                                <Col>
                                  <Form.Label className="inquiry-label">
                                    Overnight
                                  </Form.Label>
                                  <div className="d-flex justify-content-center align-items-center gap-3">
                                    <Form.Select
                                      name="overnight"
                                      value={overnight}
                                      onChange={(e) =>
                                        handleOvernight(
                                          parseInt(e.target.value)
                                        )
                                      }
                                      className="form-select"
                                      style={{ width: "90%" }}
                                      required
                                    >
                                      <option value="" default>
                                        Select An Option
                                      </option>
                                      {currentMessageListingDetails.pricing &&
                                        currentMessageListingDetails.pricing
                                          .dailyOptions &&
                                        currentMessageListingDetails.pricing
                                          .dailyOptions.length > 0
                                        ? currentMessageListingDetails.pricing.dailyOptions.map(
                                          (option) => (
                                            <option
                                              key={option.days}
                                              value={option.days}
                                            >
                                              {`${option.days} Days (${option.days * 24
                                                } hours)`}
                                            </option>
                                          )
                                        )
                                        : null}
                                    </Form.Select>
                                  </div>
                                </Col>
                              </Row>
                            </>
                          )}
                      </Row>
                    </Col>
                  </Row>
                  {currentMessageListingDetails &&
                    currentMessageListingDetails?.pricing &&
                    currentMessageListingDetails?.pricing?.hourlyOptions &&
                    currentMessageListingDetails?.pricing?.hourlyOptions?.length > 0 && (
                      <>
                        <Row>
                          <Col xs={12} className="text-center mt-3">
                            <Form.Label className="inquiry-label">
                              Hourly Packages
                            </Form.Label>
                            <div className="d-flex justify-content-center align-items-center gap-3">
                              <Form.Select
                                name="sameDayHours"
                                id="sameDayHours"
                                value={sameDayHours}
                                onChange={(e) =>
                                  handleSameDayHours(e.target.value)
                                }
                                className="form-select"
                                required
                              >
                                <option value="" default>
                                  Select An Option
                                </option>
                                {currentMessageListingDetails.pricing.hourlyOptions.map(
                                  (option) => (
                                    <option
                                      key={option.hours}
                                      value={option.hours}
                                    >
                                      {`${option.hours} Hours`}
                                    </option>
                                  )
                                )}
                              </Form.Select>
                            </div>
                          </Col>
                        </Row>
                      </>
                    )}
                  <Row className="mt-3">
                    <FormGroup className="d-flex">
                      {!isFishingCharterListing(currentMessageListingDetails) && (
                        <>
                      <h5 className="mt-3">Additionals:</h5>
                      {boatDetailsById?.deliveryAndPickup && (
                        <div className="d-flex">
                          <FormControlLabel
                            style={{ alignItems: "flex-start" }}
                            control={
                              <Checkbox
                                checked={deliveryPickupCheckbox}
                                onChange={handleDeliveryPickupCheckboxChange}
                              />
                            }
                          />
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <p
                              style={{ fontWeight: "500", lineHeight: "28px" }}
                            >
                              Price Quote for Pickup and Delivery.
                            </p>
                          </div>
                        </div>
                      )}
                      {currentMessageListingDetails &&
                        (currentMessageListingDetails.boatType ===
                          "Bass Boat" ||
                          (currentMessageListingDetails.boatType !==
                            "Bass Boat" &&
                            currentMessageListingDetails.pricing &&
                            currentMessageListingDetails.pricing
                              .overnightEnabled)) && (
                          <>
                            <div className="d-flex mt-1">
                              <FormControlLabel
                                style={{ alignItems: "flex-start" }}
                                control={
                                  <Checkbox
                                    // disabled={
                                    //   (overnight && overnight > 0) ||
                                    //   sameDayHours >= 24
                                    // } // Modified the disabled condition
                                    checked={trailerCheckbox}
                                    onChange={handleTrailerCheckbox}
                                  />
                                }
                              />
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <p
                                  style={{
                                    fontWeight: "500",
                                    lineHeight: "28px",
                                  }}
                                >
                                  Price Quote for Trailer Package.
                                  {overnight > 0 && (
                                    <span style={{ color: "red" }}> *</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </>
                        )}

                      <div className="d-flex mt-1">
                        <FormControlLabel
                          style={{ alignItems: "flex-start" }}
                          control={
                            <Checkbox
                              checked={captainCheckbox}
                              onChange={handleCaptainCheckbox}
                            />
                          }
                        />
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <p style={{ fontWeight: "500", lineHeight: "28px" }}>
                            Price Quote to Provide Captain.
                          </p>
                        </div>
                      </div>
                        </>
                      )}

                      <Row>
                        {!isFishingCharterListing(currentMessageListingDetails) && (
                        <Col xs={6}>
                          <Form.Group controlId="priceInput">
                            <Form.Label>Additional Cost</Form.Label>
                            <CurrencyInput
                              id="additionalCost"
                              name="additionalCost"
                              placeholder="Enter Price"
                              defaultValue={0}
                              decimalsLimit={2}
                              prefix="$"
                              value={additionalCost}
                              onValueChange={(value) =>
                                handleAdditionalCost(value)
                              }
                              className="form-control"
                            />
                          </Form.Group>
                        </Col>
                        )}
                        {console.log("ownerDiscount334", ownerDiscount)}
                        <Col xs={6}>
                          <Form.Group controlId="priceInput">
                            <Form.Label>Discount</Form.Label>
                            <CurrencyInput
                              id="ownerDiscount"
                              name="ownerDiscount"
                              placeholder="Enter discount"
                              defaultValue={0}
                              disable={true}
                              decimalsLimit={2}
                              prefix="$"
                              value={ownerDiscount}
                              onValueChange={(value) =>
                                handleOwnerDiscount(value)
                              }
                              className="form-control"
                            />
                          </Form.Group>
                          {error && (
                            <Alert className="mt-3" severity="error">
                              {error}
                            </Alert>
                          )}
                        </Col>
                      </Row>
                    </FormGroup>
                    <Col xs={12} className="text-center mt-3">
                      <Row>
                        <Col>
                          <Form.Label className="inquiry-label">
                            Duration: {offeredDuration}
                          </Form.Label>
                          <Form.Control
                            type="hidden"
                            name="offeredduration"
                            className="form-control"
                            value={offeredDuration}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={12} className="text-center mt-3">
                      <Row>
                        <Col>
                          <Form.Label className="inquiry-label">
                            Ariving Date & Time: {arivingDateTime}
                          </Form.Label>
                          <Form.Control
                            type="hidden"
                            name="arivingdatetime"
                            value={arivingDateTime}
                            className="form-control"
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditOfferModal}>
            Close
          </Button>
          <Button variant="primary" disabled={error} onClick={handleEditOffer}>
            {loading ? (
              <ThreeDots
                visible={true}
                height="22"
                width="22"
                color="#fff"
                radius="20"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            ) : (
              "Update Offer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pay Now Confirmation Modal for Accepted Proposed Edits */}
      <Modal
        show={showOfferReceivedGuidanceModal}
        onHide={() => closeOfferReceivedGuidanceModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>You&apos;ve got an offer!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Great news — the boat owner sent you a custom offer! Review the
            details, ask any questions in this chat, and hit Accept Offer when
            you&apos;re ready. Once you pay, your trip is locked in.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => closeOfferReceivedGuidanceModal(true)}
          >
            View my offer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showOfferSentGuidanceModal}
        onHide={() => setShowOfferSentGuidanceModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Offer Sent!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            You&apos;ll receive a notification after the renter accepts your
            offer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowOfferSentGuidanceModal(false)}
          >
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPayNowModal} onHide={() => setShowPayNowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-success" style={{ fontSize: "1.2rem" }}>
            <i className="material-icons me-2" style={{ verticalAlign: "middle", color: "#28a745", fontSize: "24px" }}>check_circle</i>
            Proposed Edit Accepted!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <p className="fw-bold mb-2" style={{ fontSize: "1.1rem", color: "#2c3e50" }}>
            Your booking details have been successfully updated!
          </p>
          <p className="text-muted small mb-4">
            Would you like to complete your payment now to secure your reservation with the updated price?
          </p>
          <div className="d-flex flex-column gap-2 px-3">
            <Button
              variant="primary"
              size="lg"
              className="w-100 fw-bold shadow-sm"
              onClick={() => {
                setShowPayNowModal(false);
                if (payNowModalMessage) {
                  handlePayNowFromProposedEdit(payNowModalMessage);
                }
              }}
            >
              Pay Now
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="w-100"
              onClick={() => setShowPayNowModal(false)}
            >
              Pay Later
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
